import React, { useState, useCallback, useRef, useEffect, useLayoutEffect } from 'react';
import { SpinnerGap, IconContext } from '@phosphor-icons/react';
import { useAuth } from './hooks/useAuth';
import { useTheme } from './hooks/useTheme';
import { useDeckTimeline, type DeckTimelineHandlers } from './hooks/useDeckTimeline';
import { interruptDeckTurn, sendMessageOnly } from './api/stream-client';
import LoginPage from './components/auth/LoginPage';
import DeckDashboard from './components/DeckDashboard';
import OnboardingTour from './components/OnboardingTour';
import { DeckTimeChip, DeckStatsPopover } from './components/StatsChip';
import Header from './components/layout/Header';
import DebugPanel from './components/DebugPanel';
import ConfluencePanel from './components/ConfluencePanel';
import DroploftPanel from './components/DroploftPanel';
import type { DroploftConnection, DroploftDocument } from './api/client';
import ChatPanel, { extractDetail } from './components/ChatPanel';
import type { ChatMessage, UploadedFile, ActivityItem, TurnRecord } from './components/ChatPanel';
import {
  applyAgentTimelineEvent,
  sortAgentTimelineItems,
  type AgentTimelineItem,
} from './agentTimeline/viewModel';
import PreviewPanel from './components/PreviewPanel';
import ConnectionBanner, { type ConnectionStatus } from './components/ConnectionBanner';
import NewMessagesBanner from './components/NewMessagesBanner';
import PlanConflictDialog from './components/plan/PlanConflictDialog';
import EditModePage from './components/edit/EditModePage';
import type { EditFile } from './components/edit/EditModePage';
import type { EditType } from './components/edit/ConfirmEditModal';
import * as api from './api/client';
import FileViewerDrawer from './components/FileViewerDrawer';
import SettingsPage from './components/SettingsPage';
import AdminDashboard from './components/AdminDashboard';
import { useIsAdmin } from './hooks/useIsAdmin';
import { NotificationBanner } from './components/NotificationBanner';
import { notifyError } from './hooks/useNotify';
import { apiFetch, AuthExpiredError } from './api/auth-fetch';
import type { Deck, Phase, AgentState, Profile } from './types';
import type { AgentTimelineEvent } from './agentTimeline/normalize';
import { useRoute, navigate } from './router';
import { DEMO_MEMORIES, DEMO_SKILLS, DEMO_CONTEXT_FILES } from './data/demoData';
// Demo data only used in dev — remove or replace with seed API for production
const USE_DEMO_DATA = import.meta.env.DEV;

const ICON_DEFAULTS = { size: 18, weight: 'light' as const, color: 'currentColor' };

function AppInner() {
  const { user, isLoading: authLoading, handleLogin, logout } = useAuth();
  const { theme, toggleTheme } = useTheme();
  // Server-resolved admin flag (PR #17). The legacy
  // `profiles[user.id].is_admin` path never gets populated on the CF
  // rebuild, so the sidebar's "Admin" link was hidden for actual
  // admins. useIsAdmin hits /api/admin/me which is the authoritative
  // source.
  const { isAdmin: isAdminFromServer } = useIsAdmin();

  // Handle Google OAuth redirect
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    if (params.get('google_auth') === 'success' && params.get('login')) {
      try {
        const loginData = JSON.parse(decodeURIComponent(params.get('login')!));
        handleLogin(loginData);
      } catch (e) {
        console.error('[google-auth] Failed to parse login data:', e);
      }
      window.history.replaceState({}, '', window.location.pathname);
    } else if (params.get('google_auth') === 'error') {
      console.error('[google-auth] OAuth error:', params.get('reason'));
      window.history.replaceState({}, '', window.location.pathname);
    }
  }, [handleLogin]);

  const [decks, setDecks] = useState<Deck[]>([]);
  const [workspaces, setWorkspaces] = useState<api.Workspace[]>([]);
  const [activeWorkspaceId, setActiveWorkspaceId] = useState<string | null>(null);
  const [activeDeck, setActiveDeck] = useState<Deck | null>(null);
  const [openingDeckId, setOpeningDeckId] = useState<string | null>(null);
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [hasMoreMessages, setHasMoreMessages] = useState(false);
  const [loadingMoreMessages, setLoadingMoreMessages] = useState(false);
  const [isCreating, setIsCreating] = useState(false);
  const [isGenerating, setIsGeneratingRaw] = useState(false);
  const [chatConnected, setChatConnected] = useState(false);
  // Connection-UX state — drives ConnectionBanner / NewMessagesBanner / generating-indicator variant.
  // - chatErrorCount bumps on onError, resets on successful onConnected.
  // - disconnectedAt is the millis timestamp of the first error in the current streak (null when connected).
  // - isOnline mirrors navigator.onLine (network-level) so we can distinguish "SSE dropped" vs "whole network is gone".
  // - backOnlineUntil is a fade-out timestamp for the transient "Back online" banner.
  // - newMessagesCount is the delta detected on reconnect between the pre-disconnect message count
  //   and the REST snapshot — drives the "N new messages" pill.
  // - nowTick forces a re-render each second when we are disconnected so threshold transitions
  //   (3s → reconnecting, 10s → lost, 30s → lost-long) fire without extra event wiring.
  const [chatErrorCount, setChatErrorCount] = useState(0);
  const [disconnectedAt, setDisconnectedAt] = useState<number | null>(null);
  const [isOnline, setIsOnline] = useState<boolean>(
    typeof navigator !== 'undefined' ? navigator.onLine : true
  );
  const [backOnlineUntil, setBackOnlineUntil] = useState<number | null>(null);
  const [newMessagesCount, setNewMessagesCount] = useState(0);
  const [nowTick, setNowTick] = useState(Date.now());
  const activeDeckIdForTimer = useRef<string>('');
  const setIsGenerating = useCallback((v: boolean) => {
    setIsGeneratingRaw(v);
    if (!v && activeDeckIdForTimer.current) {
      sessionStorage.removeItem(`pp_activity_start_${activeDeckIdForTimer.current}`);
    }
  }, []);
  const [activityItems, setActivityItems] = useState<ActivityItem[]>([]);
  const [agentTimelineItems, setAgentTimelineItems] = useState<AgentTimelineItem[]>([]);
  /**
   * Per-turn grouped view of activity. Each turn aggregates its activity
   * items and gains a `summary` + `durationMs` once Arx emits `done`.
   * The flat `activityItems` list above is kept for components (e.g.
   * preview / right-panel status) that still consume the legacy shape.
   */
  const [turns, setTurns] = useState<TurnRecord[]>([]);
  const activeActivityTurnIdRef = useRef<string | null>(null);
  const pendingLocalActivityTurnIdRef = useRef<string | null>(null);
  /** Clears both the flat activity stream and the grouped per-turn view. */
  const clearActivity = useCallback(() => {
    activeActivityTurnIdRef.current = null;
    pendingLocalActivityTurnIdRef.current = null;
    setActivityItems([]);
    setTurns([]);
  }, []);
  // Derived string for right-panel components that still expect a simple status text
  const currentActivity = activityItems.length > 0
    ? activityItems[activityItems.length - 1].detail
    : '';
  const bindLocalActivityTurn = useCallback((realTurnId: string, agent = 'pp-writer', agentId = 'pp-writer') => {
    if (!realTurnId) return;
    const localTurnId = pendingLocalActivityTurnIdRef.current;
    activeActivityTurnIdRef.current = realTurnId;
    if (!localTurnId || localTurnId === realTurnId) {
      pendingLocalActivityTurnIdRef.current = null;
      return;
    }
    pendingLocalActivityTurnIdRef.current = null;
    setActivityItems(prev => prev.map(item =>
      item.turnId === localTurnId ? { ...item, turnId: realTurnId, agentId } : item
    ));
    setAgentTimelineItems(prev => prev.map(item =>
      item.turnId === localTurnId
        ? { ...item, turnId: realTurnId, agent, agentId }
        : item
    ));
    setTurns(prev => {
      const localIdx = prev.findIndex(t => t.turnId === localTurnId);
      if (localIdx < 0) return prev;
      const realIdx = prev.findIndex(t => t.turnId === realTurnId);
      const localTurn = prev[localIdx];
      if (realIdx >= 0) {
        return prev.reduce<TurnRecord[]>((acc, turn, idx) => {
          if (idx === localIdx) return acc;
          if (idx === realIdx) {
            acc.push({
              ...turn,
              agent,
              agentId,
              items: [
                ...localTurn.items.map(item => ({ ...item, turnId: realTurnId, agentId })),
                ...turn.items,
              ],
              startedAt: Math.min(turn.startedAt, localTurn.startedAt),
            });
            return acc;
          }
          acc.push(turn);
          return acc;
        }, []);
      }
      return prev.map((turn, idx) => idx === localIdx
        ? {
            ...turn,
            turnId: realTurnId,
            agent,
            agentId,
            items: turn.items.map(item => ({ ...item, turnId: realTurnId, agentId })),
          }
        : turn);
    });
  }, []);

  const seedLocalActivityTurn = useCallback((opts: { append?: boolean } = {}) => {
    const turnId = `local-turn-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
    const ts = Date.now();
    const item: ActivityItem = {
      id: `act-${ts}-${Math.random().toString(36).slice(2, 6)}`,
      tool: 'queued',
      detail: 'Queued',
      status: 'running',
      ts,
      turnId,
      agentId: 'pp-writer',
    };
    pendingLocalActivityTurnIdRef.current = turnId;
    if (!opts.append) activeActivityTurnIdRef.current = turnId;
    setActivityItems(prev => opts.append ? [...prev, item] : [item]);
    const turn: TurnRecord = {
      turnId,
      agent: 'pp-writer',
      agentId: 'pp-writer',
      items: [item],
      status: 'live',
      startedAt: ts,
    };
    setTurns(prev => opts.append ? [...prev, turn] : [turn]);
    const queuedTimelineItem: AgentTimelineItem = {
      id: `tool:${turnId}:queued`,
      kind: 'agent.tool',
      turnId,
      ts,
      agent: 'pp-writer',
      agentId: 'pp-writer',
      title: 'Sending',
      detail: 'Waiting for the server to accept this message.',
      status: 'running',
      category: 'system',
      raw: { localQueued: true },
    };
    setAgentTimelineItems(prev => sortAgentTimelineItems([...prev, queuedTimelineItem]));
    return turnId;
  }, []);

  const bindOptimisticUserTimelineTurn = useCallback((timelineIds: string[], turnId: string) => {
    if (!turnId || timelineIds.length === 0) return;
    const idSet = new Set(timelineIds);
    setAgentTimelineItems(prev => sortAgentTimelineItems(prev.map(item => {
      if (item.kind !== 'chat.user' || item.turnId || !idSet.has(item.id)) return item;
      return { ...item, turnId };
    })));
  }, []);

  const updateLocalQueuedTimeline = useCallback((
    turnId: string,
    update: Pick<Extract<AgentTimelineItem, { kind: 'agent.tool' }>, 'title' | 'detail' | 'status'>,
  ) => {
    if (!turnId) return;
    setAgentTimelineItems(prev => sortAgentTimelineItems(prev.map(item => {
      const raw = item.raw && typeof item.raw === 'object' ? item.raw as { localQueued?: boolean } : null;
      if (!raw?.localQueued || item.turnId !== turnId || item.kind !== 'agent.tool') return item;
      return { ...item, ...update };
    })));
  }, []);
  const [activityError, setActivityError] = useState(false);
  const [planTask, setPlanTask] = useState<'create' | 'update' | null>(null);
  const [previewTask, setPreviewTask] = useState<'create' | 'update' | null>(null);
  const [notesTask, setNotesTask] = useState<'create' | 'update' | null>(null);
  const [appLoading, setAppLoading] = useState(true);
  const [chatCollapsed, setChatCollapsed] = useState(false);
  // Golden ratio layout — chat/panel ratio flips by phase
  const PHI = 1.618;
  const [chatWidth, setChatWidth] = useState(window.innerWidth);
  const [userDragged, setUserDragged] = useState(false);
  // Right panel starts open — shows concept cloud by default
  const [panelOpen, setPanelOpen] = useState(false);
  const [debugOpen, setDebugOpen] = useState(false);
  const [confluenceOpen, setConfluenceOpen] = useState(false);
  const [droploftOpen, setDroploftOpen] = useState(false);
  // Active-time chip in the top nav. statsChipRef anchors the popover
  // to the chip's bounding rect; click-outside dismisses.
  const [statsOpen, setStatsOpen] = useState(false);
  const statsChipRef = useRef<HTMLButtonElement>(null);
  const [droploftConnections, setDroploftConnections] = useState<DroploftConnection[]>([]);
  const [droploftDocuments, setDroploftDocuments] = useState<DroploftDocument[]>([]);
  const [droploftLoading, setDroploftLoading] = useState(false);
  const [droploftImporting, setDroploftImporting] = useState<Set<string>>(new Set());
  const [droploftImported, setDroploftImported] = useState<Set<string>>(new Set());
  const [droploftPublishing, setDroploftPublishing] = useState(false);
  const [planMarkdown, setPlanMarkdown] = useState('');
  const [planSaveStatus, setPlanSaveStatus] = useState<'idle' | 'saving' | 'saved'>('idle');
  const [planVersions, setPlanVersions] = useState<{ key: string; label: string }[]>([]);
  const [activePlanKey, setActivePlanKey] = useState('memory/storyboard.md');
  const [notesMarkdown, setNotesMarkdown] = useState('');
  const [notesSaveStatus, setNotesSaveStatus] = useState<'idle' | 'saving' | 'saved'>('idle');
  const [notesVersions, setNotesVersions] = useState<{ key: string; label: string }[]>([]);
  const [activeNotesKey, setActiveNotesKey] = useState('memory/speaker-script.md');
  const [storyAudience, setStoryAudience] = useState('');
  const [storyNarrative, setStoryNarrative] = useState('');
  const [contextItems, setContextItems] = useState<api.ContextItem[]>([]);
  const [editMode, setEditMode] = useState<{ files: EditFile[]; activeFile: string | null } | null>(null);
  const [viewerFile, setViewerFile] = useState<{ name: string; content: string } | null>(null);
  const [agentStatuses, setAgentStatuses] = useState<Record<string, AgentState>>({});
  const [deckMembers, setDeckMembers] = useState<api.DeckMember[]>([]);
  const [pendingToggleKeys, setPendingToggleKeys] = useState<Set<string>>(new Set());
  const [isDragging, setIsDragging] = useState(false);
  const PHASE_ORDER: Phase[] = ['gather', 'plan', 'plan-ready', 'preview'];
  const phaseIdx = (p: Phase) => PHASE_ORDER.indexOf(p);
  const [maxPhaseReached, setMaxPhaseReached] = useState<Phase>('gather');
  const [previewRefreshKey, setPreviewRefreshKey] = useState(0);
  // Right-side preview panel: when set, the panel renders an artifact view
  // (file metadata + Download / inline preview for image+pdf) overlaid on
  // top of the deck-html iframe. Cleared when the user clicks "Back to deck".
  const [artifactView, setArtifactView] = useState<{
    fileHash?: string;
    fileUrl?: string;
    filename: string;
    fileMime?: string;
    fileSize?: number;
  } | null>(null);
  const [activeSlideHash, setActiveSlideHash] = useState<string>('');
  const [conceptCloudVersion, setConceptCloudVersion] = useState(0);
  const [artifacts, setArtifacts] = useState<api.ArtifactItem[]>([]);
  const [activeArtifactKey, setActiveArtifactKey] = useState<string | null>(null);
  const [profiles, setProfiles] = useState<Record<string, Profile>>({});
  const route = useRoute();
  // Forward ref to selectDeck (defined far below) so this back/forward
  // route effect can reuse its full clear+load flow. Assigned right
  // after selectDeck's definition each render.
  const selectDeckRef = useRef<
    ((id: string, prefetched?: Deck, opts?: { skipNavigate?: boolean }) => Promise<void>) | null
  >(null);
  const routeDeckRestoreRef = useRef<{ deckId: string | null; inFlight: boolean }>({
    deckId: null,
    inFlight: false,
  });

  // Respond to browser back/forward — sync state with route
  useEffect(() => {
    if (route.page === 'workspace') {
      setOpeningDeckId(null);
      // Navigated back to workspace — clear ALL deck state
      if (activeDeck) {
        setActiveDeck(null);
        setIsGenerating(false);
        clearActivity();
        setMessages([]);
        setAgentTimelineItems([]);
        setPlanMarkdown('');
        setNotesMarkdown('');
        setStoryAudience('');
        setStoryNarrative('');
        setContextItems([]);
        setContextHydratedDeckId(null);
        setArtifacts([]);
        setPanelOpen(false);
        setUserDragged(false);
        setPlanTask(null);
        setNotesTask(null);
        setPreviewTask(null);
        setPlanVersions([]);
        setNotesVersions([]);
        setActivePlanKey('memory/storyboard.md');
        setActiveNotesKey('memory/speaker-script.md');
        setActiveArtifactKey(null);
        setEditMode(null);
        setViewerFile(null);
        setDeckMembers([]);
        setPendingToggleKeys(new Set());
        setMaxPhaseReached('gather');
        setAgentStatuses({});
        setChatConnected(false);
      }
      // Sync workspace from URL
      if (route.params.workspaceId && route.params.workspaceId !== activeWorkspaceId) {
        setActiveWorkspaceId(route.params.workspaceId);
        api.listWorkspaceDecks(route.params.workspaceId).then(setDecks).catch(() => {});
      }
    } else if (route.page === 'deck' && route.params.deckId) {
      if (
        routeDeckRestoreRef.current.inFlight &&
        routeDeckRestoreRef.current.deckId === route.params.deckId
      ) {
        return;
      }
      // Navigated forward to a deck (e.g. forward button). Skip if
      // selectDeck has already claimed this deckId — `activeDeckIdRef`
      // is set synchronously inside selectDeck before navigate() fires,
      // so a matching ref means the deck is being hydrated from a
      // pre-fetched Deck object (PR #119 hot path). Without this
      // guard the route-effect runs an independent `api.getDeck`
      // before React has propagated `setActiveDeck`, which adds a
      // ~2s parallel call for nothing on the new-deck path.
      if (activeDeckIdRef.current === route.params.deckId) {
        return;
      }
      if (!activeDeck || activeDeck.id !== route.params.deckId) {
        // Back/forward to a different deck. Route through selectDeck so
        // per-deck state (messages, activity, artifacts, plan/notes, …)
        // is cleared and reloaded — identical to a deck-list click.
        // skipNavigate because popstate already set the URL. Without
        // this the previous deck's chat/workspace lingered (it only
        // setActiveDeck, never clearing or reloading messages).
        void selectDeckRef.current?.(route.params.deckId, undefined, { skipNavigate: true });
      }
    }
  }, [route.page, route.params.workspaceId, route.params.deckId]);

  // Confluence integration state
  const [confluenceConnections, setConfluenceConnections] = useState<any[]>([]);
  const [confluenceSpaces, setConfluenceSpaces] = useState<any[]>([]);
  // Non-null when the most-recent listSpaces call failed (Atlassian
  // 5xx, token rejected, network hiccup). Lets the panel distinguish
  // "no spaces" from "couldn't load" + render the Retry button.
  const [confluenceSpacesError, setConfluenceSpacesError] = useState<string | null>(null);
  const [confluencePages, setConfluencePages] = useState<any[]>([]);
  const [confluenceLoading, setConfluenceLoading] = useState(false);
  const [selectedSpaceId, setSelectedSpaceId] = useState<string>('');
  const [referencingPageIds, setReferencingPageIds] = useState<Set<string>>(new Set());
  // Confluence tree state (hierarchical lazy-loading)
  const [confluenceExpandedPages, setConfluenceExpandedPages] = useState<Set<string>>(new Set());
  const [confluenceChildPages, setConfluenceChildPages] = useState<Map<string, any[]>>(new Map());
  const [confluenceLoadingChildren, setConfluenceLoadingChildren] = useState<Set<string>>(new Set());

  // Compute max reachable phase from content (for UI tab visibility only).
  // Does NOT change the deck's stored phase — that's only advanced via advancePhase API.
  const computeMaxPhase = (ctx: api.ContextItem[], arts: api.ArtifactItem[]): Phase => {
    const hasHTML = arts.some(a => a.filename?.endsWith('.html'));
    if (hasHTML) return 'preview';
    const hasStoryboard = ctx.some(c => c.key === 'memory/storyboard.md' && c.content);
    if (hasStoryboard) return 'plan-ready';
    const hasPitchBrief = ctx.some(c => c.key === 'memory/pitch-brief' && c.content);
    if (hasPitchBrief) return 'plan';
    return 'gather';
  };

  const normalizePhase = (phase?: string | null): Phase => {
    if (phase === 'generate') return 'preview';
    if (phase === 'plan' || phase === 'plan-ready' || phase === 'preview') return phase;
    return 'gather';
  };

  const hasDeckSignalPreview = (deck?: Pick<Deck, 'signals'> | null): boolean => {
    return !!deck?.signals?.artifacts?.some(a =>
      a.kind === 'deck-group'
      && (
        a.source_format === 'html'
        || a.mime === 'text/html'
        || a.preview_url.includes('/preview')
      )
    );
  };

  const computePanelPhase = (
    deck: Pick<Deck, 'phase' | 'signals'> | null | undefined,
    ctx: api.ContextItem[],
    arts: api.ArtifactItem[],
  ): Phase => {
    const contentPhase = computeMaxPhase(ctx, arts);
    const deckPhase = normalizePhase(deck?.phase);
    if (hasDeckSignalPreview(deck)) return 'preview';
    return phaseIdx(deckPhase) > phaseIdx(contentPhase) ? deckPhase : contentPhase;
  };

  // Phase → tulpa mapping (mirrors server's phase-manager.mjs)
  const getTulpaForPhase = (_ph: Phase): string => 'pp-writer';

  // Resolve the deck's Mosoo Thread without blocking deck open.
  const [isWarmingUp, setIsWarmingUp] = useState(false);
  const [warmupPhase, setWarmupPhase] = useState<'booting' | 'connecting' | 'error'>('booting');
  const [warmupError, setWarmupError] = useState<string | null>(null);
  const warmupTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const warmupDeckIdRef = useRef<string | null>(null);
  // True once the active deck has a usable Mosoo Thread.
  const agentWarmRef = useRef(false);

  // Called by SSE handler when agent reaches ready state — dismiss overlay
  const onAgentReady = useCallback(() => {
    if (warmupTimeoutRef.current) {
      clearTimeout(warmupTimeoutRef.current);
      warmupTimeoutRef.current = null;
    }
    setIsWarmingUp(false);
    setWarmupError(null);
  }, []);

  // Thread setup is intentionally non-blocking so existing deck content
  // remains visible while the Mosoo runtime is connected.
  const warmupAndReady = useCallback(async (deckId: string, _tulpa?: string) => {
    setWarmupPhase('booting');
    setWarmupError(null);
    warmupDeckIdRef.current = deckId;
    agentWarmRef.current = false;
    const abortController = new AbortController();
    warmupAbortRef.current = abortController;
    try {
      const members = await api.listDeckMembers(deckId);
      if (abortController.signal.aborted) return;
      setDeckMembers(members);
    } catch {
      if (abortController.signal.aborted) return;
    }

    try {
      const mosooBinding = await api.ensureDeckMosooThread(deckId);
      if (abortController.signal.aborted) return;
      if (!mosooBinding) throw new Error('Mosoo Thread is required for this deck.');
      agentWarmRef.current = true;
      onAgentReady();
    } catch (err: unknown) {
      if (abortController.signal.aborted) return;
      const msg = err instanceof Error ? err.message : 'Unknown error';
      setWarmupError(`Failed to connect Mosoo: ${msg}`);
      setWarmupPhase('error');
      warmupAbortRef.current = null;
    } finally {
      warmupAbortRef.current = null;
    }
  }, []);

  // Retry warmup after error
  const handleRetryWarmup = useCallback(() => {
    const deckId = warmupDeckIdRef.current;
    if (deckId) warmupAndReady(deckId);
  }, [warmupAndReady]);

  // Cancel warmup and return to dashboard
  const handleCancelWarmup = useCallback(() => {
    if (warmupAbortRef.current) warmupAbortRef.current.abort();
    if (warmupTimeoutRef.current) { clearTimeout(warmupTimeoutRef.current); warmupTimeoutRef.current = null; }
    setIsWarmingUp(false);
    setWarmupError(null);
    setActiveDeck(null);
    setIsGenerating(false);
    clearActivity();
    setMessages([]);
    setAgentTimelineItems([]);
    setPlanMarkdown('');
    setNotesMarkdown('');
    setStoryAudience('');
    setStoryNarrative('');
    setContextItems([]);
    setContextHydratedDeckId(null);
    setArtifacts([]);
    setPanelOpen(false);
    setUserDragged(false);
    setPlanTask(null);
    setNotesTask(null);
    setPreviewTask(null);
    setPlanVersions([]);
    setNotesVersions([]);
    setActivePlanKey('memory/storyboard.md');
    setActiveNotesKey('memory/speaker-script.md');
    setActiveArtifactKey(null);
    setEditMode(null);
    setViewerFile(null);
    setDeckMembers([]);
    setPendingToggleKeys(new Set());
    setMaxPhaseReached('gather');
    setAgentStatuses({});
    setChatConnected(false);
    setChatWidth(window.innerWidth);
    navigate('/');
  }, []);

  /** Derive display label from profile, fallback to tulpa_id */
  const tulpaDisplayName = (tulpaId: string): string => {
    const p = profiles[tulpaId];
    if (p?.display_name) return p.display_name;
    const name = tulpaId.startsWith('pp-') ? tulpaId.slice(3) : tulpaId;
    return name.charAt(0).toUpperCase() + name.slice(1);
  };

  const currentTulpa = getTulpaForPhase(activeDeck?.phase || 'gather');
  const currentTulpaLabel = tulpaDisplayName(currentTulpa);
  // With the SDK lifecycle, agents are created on demand at tasks.send time
  // (server returns 503/Retry-After while booting; SDK retries transparently).
  // Default to 'idle' when no SSE status has arrived yet — chat is ready and
  // the first send will trigger boot under the hood. SSE program_status events
  // overwrite this once the agent actually transitions.
  const agentState: AgentState = agentStatuses[currentTulpa] || 'idle';
  const agentOnline = agentState === 'idle' || agentState === 'active';
  const agentBooting = agentState === 'booting';

  const warmupAbortRef = useRef<AbortController | null>(null);
  // Optimistic local user message awaiting SSE confirmation — allows id swap when the
  // server's user_message broadcast arrives so subsequent dedups match on real id.
  const pendingLocalMessageRef = useRef<{ localId: string; content: string } | null>(null);
  const dividerRef = useRef<HTMLDivElement>(null);
  // planSaveTimer removed — manual save only, no debounce
  const planDirtyRef = useRef(false);
  const planLastSavedRef = useRef('');
  const [pendingAgentPlan, setPendingAgentPlan] = useState<string | null>(null);
  const activeDeckIdRef = useRef<string | null>(null);
  const sendMessageRef = useRef<((msg: string, opts?: { displayContent?: string; messageType?: 'file' | 'folder'; skipLocalMessage?: boolean }) => void) | null>(null);

  // Extract audience label from either string or object format
  const extractAudienceLabel = (audience: unknown): string => {
    if (typeof audience === 'string') return audience;
    if (audience && typeof audience === 'object') {
      const a = audience as Record<string, unknown>;
      return (a.name as string) || (a.audience as string) || '';
    }
    return '';
  };

  // Hidden messages that should not appear in chat history
  const HIDDEN_USER_MESSAGES = new Set(['start', 'build', 'plan']);

  // Message type prefix convention: [REF:file], [REF:folder]
  // Also handle legacy [Attached files: context/name1, context/name2] format
  const REF_LINE_RE = /^\[REF:(file|folder)\]\s*(.+)$/m;
  const ATTACHED_RE = /^\[Attached files:\s*(.+)\]$/;

  /**
   * Expand a single message that may contain [REF:xxx] lines into multiple ChatMessages.
   * E.g. "[REF:file] foo.md\n[REF:file] bar.html\nHello" → 3 messages.
   */
  const expandRefMessage = (m: { id: string; role: string; content: string; timestamp: number }): ChatMessage[] => {
    const lines = (m.content || '').split('\n');
    const result: ChatMessage[] = [];
    const textLines: string[] = [];
    let refIdx = 0;

    for (const line of lines) {
      const refMatch = line.match(REF_LINE_RE);
      if (refMatch) {
        const kind = refMatch[1] as 'file' | 'folder';
        const target = refMatch[2].trim();
        // For [REF:file] <key>, the captured target is the context
        // key (e.g. `memory/storyboard.md`, `artifacts/v1/deck.pptx`).
        // Surface it as `fileKey` so renderFileCard knows how to
        // build the path-addressed download URL — rehydrated history
        // cards would otherwise be unclickable.
        result.push({
          id: `${m.id}-ref-${refIdx++}`,
          role: m.role as 'user' | 'assistant' | 'system',
          content: line,
          displayContent: target,
          messageType: kind,
          ...(kind === 'file' ? { fileKey: target } : {}),
          timestamp: m.timestamp + refIdx,
        });
      } else if (line.trim()) {
        textLines.push(line);
      }
    }

    if (textLines.length > 0) {
      result.push({
        id: `${m.id}-text`,
        role: m.role as 'user' | 'assistant' | 'system',
        content: textLines.join('\n'),
        timestamp: m.timestamp + refIdx + 1,
      });
    }

    return result;
  };

  /**
   * Flatten the multimodal `contentBlocks` shape from the server into
   * one ChatMessage per logical chat bubble: text blocks coalesce into
   * a single text bubble per message; file blocks each become their own
   * file-card bubble carrying the content-addressed hash so the card
   * is clickable.
   *
   * Legacy `[REF:file]` text markers (still produced by the user
   * attachment send path until that also migrates to file blocks) are
   * expanded by `expandRefMessage` exactly as before — the markers now
   * arrive inside a single text block, but the parsing target is the
   * concatenated text, not the wire shape.
   */
  const blocksToMessages = (m: any): ChatMessage[] => {
    const blocks: any[] = Array.isArray(m.contentBlocks) ? m.contentBlocks : [];
    const ts = new Date(m.created_at).getTime();
    const out: ChatMessage[] = [];

    // Coalesce all text blocks into a single string, in order. Any
    // [REF:file] lines inside that text get the legacy expansion below.
    const textChunks: string[] = [];
    let textIdx = 0;
    let fileIdx = 0;
    for (const b of blocks) {
      if (b && b.kind === 'text' && typeof b.text === 'string') {
        textChunks.push(b.text);
      } else if (b && b.kind === 'file') {
        // Flush any text seen so far first so card order matches wire order.
        if (textChunks.length > 0) {
          const joined = textChunks.join('');
          textChunks.length = 0;
          out.push(...textOrRefMessages(m, joined, ts, textIdx++));
        }
        out.push({
          id: `${m.id}-file-${fileIdx++}`,
          role: m.role,
          // `content` keeps the filename so existing renderers that
          // read `displayContent || content` still show something.
          content: typeof b.name === 'string' ? b.name : 'file',
          displayContent: typeof b.name === 'string' ? b.name : 'file',
          messageType: 'file',
          fileHash: typeof b.hash === 'string' ? b.hash : undefined,
          fileMime: typeof b.mime === 'string' ? b.mime : undefined,
          fileSize: typeof b.size === 'number' ? b.size : undefined,
          timestamp: ts + fileIdx,
          agent: m.role === 'assistant' ? (m.agent || undefined) : undefined,
        });
      }
      // Unknown / future block kinds are silently dropped — the SDK
      // forward-compat type carries the raw shape, but there's no UX
      // for it here yet.
    }
    if (textChunks.length > 0) {
      const joined = textChunks.join('');
      out.push(...textOrRefMessages(m, joined, ts, textIdx));
    }

    return out;
  };

  /** Apply the legacy [Attached files] / [REF:file] expansion to a flat text body. */
  const textOrRefMessages = (m: any, body: string, ts: number, suffix: number): ChatMessage[] => {
    let text = body;
    if (ATTACHED_RE.test(text)) {
      const match = text.match(ATTACHED_RE);
      if (match) {
        const files = match[1].split(',').map((f: string) => f.trim().replace(/^context\//, ''));
        const refLines = files.map((f: string) => `[REF:file] ${f}`).join('\n');
        const rest = text.replace(ATTACHED_RE, '').trim();
        text = refLines + (rest ? '\n' + rest : '');
      }
    }
    if (REF_LINE_RE.test(text)) {
      return expandRefMessage({
        id: `${m.id}-t${suffix}`,
        role: m.role,
        content: text,
        timestamp: ts + suffix,
      });
    }
    if (!text) {
      // Image-only message: still emit a bubble so the attachments
      // are visible after rehydrate. No text, no agent label.
      if (m._imageAttachments && Array.isArray(m._imageAttachments) && m._imageAttachments.length > 0) {
        return [{
          id: suffix === 0 ? m.id : `${m.id}-t${suffix}`,
          role: m.role,
          content: '',
          timestamp: ts + suffix,
          attachments: m._imageAttachments,
        }];
      }
      return [];
    }
    return [{
      id: suffix === 0 ? m.id : `${m.id}-t${suffix}`,
      role: m.role,
      content: text,
      timestamp: ts + suffix,
      agent: m.role === 'assistant' ? (m.agent || undefined) : undefined,
      // Only attach to the *first* text bubble in the message so a
      // user message with text + 2 images doesn't duplicate the
      // thumbnails on every chunk.
      ...(suffix === 0 && Array.isArray(m._imageAttachments) && m._imageAttachments.length > 0
        ? { attachments: m._imageAttachments }
        : {}),
    }];
  };

  /** Transform raw Arx messages into ChatMessage format, with filtering and enrichment */
  const transformMessages = (msgs: any[]): ChatMessage[] => {
    const flatTextOf = (m: any): string => {
      const blocks: any[] = Array.isArray(m?.contentBlocks) ? m.contentBlocks : [];
      return blocks
        .filter((b) => b && b.kind === 'text' && typeof b.text === 'string')
        .map((b) => b.text)
        .join('');
    };

    // Deduplicate consecutive user messages with identical text (Arx may store duplicates).
    const deduped = msgs.filter((m, i) => {
      if (i === 0) return true;
      const prev = msgs[i - 1];
      if (m.role === 'user' && prev.role === 'user' && flatTextOf(m) === flatTextOf(prev)) {
        return false;
      }
      return true;
    });

    return deduped
      .filter((m) => {
        const text = flatTextOf(m);
        if (m.role === 'user' && HIDDEN_USER_MESSAGES.has(text)) return false;
        if (m.role === 'user' && text.startsWith('[Resume]')) return false;
        return true;
      })
      .flatMap(blocksToMessages);
  };

  // (Legacy buildGatherSeedMarkdown removed — data now comes from Arx memory)

  // Load deck-specific data from Arx memory (single source of truth).
  // `prefetched` lets the caller pass arrays it has already fetched
  // (e.g. selectDeck's Promise.all batch) so we skip the in-line GETs
  // — avoids the 2x listContext / 2x getArtifacts that previously
  // ran on every selectDeck cycle.
  const loadDeckData = useCallback(async (
    deck: Deck,
    prefetched?: { ctx?: api.ContextItem[]; arts?: api.ArtifactItem[] },
  ) => {
    planDirtyRef.current = false;
    setPendingAgentPlan(null);
    setPlanMarkdown('');
    setNotesMarkdown('');
    setStoryAudience('');
    setStoryNarrative('');

    // Restore task state from DB (survives page refresh)
    if (deck.active_task) {
      const [category, action] = deck.active_task.split(':') as [string, string];
      if (category === 'plan') setPlanTask(action as 'create' | 'update');
      else if (category === 'notes') setNotesTask(action as 'create' | 'update');
      else if (category === 'preview') setPreviewTask(action as 'create' | 'update');
      setIsGenerating(true);
    } else {
      setIsGenerating(false);
      setPlanTask(null);
      setNotesTask(null);
      setPreviewTask(null);
    }

    // Load context items from Arx memory (or use prefetched)
    try {
      const ctx = prefetched?.ctx ?? await api.listContext(deck.id);
      setContextItems(ctx);

      // Storyboard — collect all versions
      const storyboards = ctx.filter(c => c.key.includes('storyboard') && c.key.endsWith('.md'));
      setPlanVersions(storyboards.map(c => ({
        key: c.key,
        label: c.key === 'memory/storyboard.md' ? 'Default'
          : c.key.replace(/^memory\//, '').replace(/\.md$/, '').replace(/^storyboard-?/, '').replace(/-/g, ' ') || 'Default',
      })));
      const activePlan = storyboards.find(c => c.key === activePlanKey) || storyboards[0];
      if (activePlan?.content) {
        setPlanMarkdown(activePlan.content);
        planLastSavedRef.current = activePlan.content;
        setActivePlanKey(activePlan.key);
      }

      // Speaker notes — collect all versions
      const scripts = ctx.filter(c => c.key.includes('speaker-script') && c.key.endsWith('.md'));
      setNotesVersions(scripts.map(c => ({
        key: c.key,
        label: c.key === 'memory/speaker-script.md' ? 'Default'
          : c.key.replace(/^memory\//, '').replace(/\.md$/, '').replace(/^speaker-script-?/, '').replace(/-/g, ' ') || 'Default',
      })));
      const activeNotes = scripts.find(c => c.key === activeNotesKey) || scripts[0];
      if (activeNotes?.content) {
        setNotesMarkdown(activeNotes.content);
        setActiveNotesKey(activeNotes.key);
      }

      // Audience + narrative (from memory/audience.json)
      const audienceCtx = ctx.find(c => c.key === 'memory/audience.json');
      if (audienceCtx?.content) {
        try {
          const a = JSON.parse(audienceCtx.content);
          const label = extractAudienceLabel(a);
          if (label) setStoryAudience(label);
          if (a.narrative) setStoryNarrative(a.narrative);
        } catch {}
      }
    } catch {
      setContextItems([]);
    }

    // Load artifacts (or use prefetched)
    let arts: api.ArtifactItem[] = [];
    try {
      arts = prefetched?.arts ?? await api.getArtifacts(deck.id);
      setArtifacts(arts);
    } catch {
      setArtifacts([]);
    }

    // Derive phase from content and update maxPhaseReached
    setContextItems(prev => {
      const derived = computePanelPhase(deck, prev, arts);
      setMaxPhaseReached(old => phaseIdx(derived) > phaseIdx(old) ? derived : old);
      // If HTML artifacts exist, ensure preview iframe loads
      if (derived === 'preview') {
        setPreviewRefreshKey(prev => prev > 0 ? prev : 1);
      }
      return prev;
    });

  }, []);

  // Load decks on mount
  useEffect(() => {
    if (!user) {
      // Cookie-only auth: there's nothing to probe locally. Once
      // useAuth's me() resolves, user becomes either truthy or null;
      // until then we just stay in loading state managed by useAuth.
      setAppLoading(false);
      return;
    }
    (async () => {
      try {
        // Fetch workspaces, decks, and profiles in parallel
        let [d, ws, profileList] = await Promise.all([
          api.listDecks(),
          api.listWorkspaces().catch(() => [] as api.Workspace[]),
          api.listProfiles().catch(() => [] as Profile[]),
        ]);
        setDecks(d);
        setWorkspaces(ws);
        // Index profiles by id for O(1) lookup
        const profileMap: Record<string, Profile> = {};
        for (const p of profileList) profileMap[p.id] = p;
        setProfiles(profileMap);

        const currentPath = window.location.pathname;

        if (ws.length > 0) {
          // Restore workspace from URL, or default to the first available workspace.
          const wsMatch = currentPath.match(/^\/w\/([^/]+)\/?$/);
          const routeWsId = wsMatch ? wsMatch[1] : null;
          const matchedWs = routeWsId ? ws.find(w => w.id === routeWsId) : null;
          if (matchedWs) {
            setActiveWorkspaceId(matchedWs.id);
            // Load workspace-specific decks
            try {
              const wsDecks = await api.listWorkspaceDecks(matchedWs.id);
              setDecks(wsDecks);
              d = wsDecks;
            } catch {}
          } else {
            const defaultWsId = ws[0].id;
            setActiveWorkspaceId(defaultWsId);
            // Sync URL to reflect current workspace
            if (!currentPath.startsWith('/deck/') && !currentPath.startsWith('/settings') && !currentPath.startsWith('/admin')) {
              navigate(`/w/${defaultWsId}`, { replace: true });
            }
          }
        }
        // Restore deck directly from URL
        const deckMatch = currentPath.match(/^\/deck\/([^/]+)\/?$/);
        const routeDeckId = deckMatch ? deckMatch[1] : null;
        if (routeDeckId) {
          if (
            routeDeckRestoreRef.current.inFlight &&
            routeDeckRestoreRef.current.deckId === routeDeckId
          ) {
            return;
          }
          routeDeckRestoreRef.current = { deckId: routeDeckId, inFlight: true };
          activeDeckIdRef.current = routeDeckId;
          setOpeningDeckId(routeDeckId);
          try {
            const found = await api.getDeck(routeDeckId);
            setActiveDeck(found);
            setMaxPhaseReached(computePanelPhase(found, [], []));

            // Start warmup in parallel with data loading — don't wait for messages/context first
            const warmupPromise = warmupAndReady(found.id);

            const PAGE_SIZE = 50;
            const [msgs, ctx, arts, versionsResp] = await Promise.all([
              api.getMessages(found.id, PAGE_SIZE, 0),
              api.listContext(found.id).catch(() => [] as api.ContextItem[]),
              api.getArtifacts(found.id).catch(() => [] as api.ArtifactItem[]),
              api.listVersions(found.id).catch(
                () => ({ versions: [], current: '' }) as Awaited<ReturnType<typeof api.listVersions>>,
              ),
            ]);
            // arx returns newest first (DESC) — reverse to chronological for display
            const chronological = Array.isArray(msgs) ? [...msgs].reverse() : [];
            const transformedMsgs = transformMessages(chronological);
            setMessages(transformedMsgs);
            setMessagesLoaded({ deckId: found.id, count: transformedMsgs.length });
            setContextHydratedDeckId(found.id);
            // CF-rebuild signal: versions exist on the deck → tell
            // PreviewPanel to fetch them and surface the iframe.
            if (versionsResp.versions.length > 0) {
              setPreviewRefreshKey(prev => Math.max(prev, 1));
            }
            await loadDeckData(found, { ctx, arts });
            resumeDataRef.current = { deck: found, ctx, arts, msgs: transformedMsgs };

            if ((found.phase as string) === 'generate') {
              api.advancePhase(found.id, 'preview').then(u => setActiveDeck(u)).catch(() => {});
            }
            void warmupPromise.catch(() => {});
          } catch {
            // Deck not found or auth failed — redirect to workspace
            navigate('/', { replace: true });
          } finally {
            setOpeningDeckId(null);
            routeDeckRestoreRef.current = { deckId: routeDeckId, inFlight: false };
          }
        }
      } catch {}
      setAppLoading(false);
    })();
  }, [user, loadDeckData]);

  // Real-time deck event handlers. Subscribed through the unified
  // deck timeline hook below so the app opens one SSE connection per deck.
  const deckTimelineEventHandlers: DeckTimelineHandlers = {
    onAgentStatus: (status, tulpaName) => {
      // agent_status webhook is container-level (booting/running/stopped).
      // It's not the authoritative source — program_status is. Ignore here.
      // (Only used as a hint that the agent is progressing.)
      if (status === 'booting') {
        const tulpa = tulpaName || getTulpaForPhase(activeDeck?.phase || 'gather');
        setAgentStatuses(prev => ({ ...prev, [tulpa]: prev[tulpa] || 'booting' }));
      }
    },
    onAgentProgramStatus: (programStatus, tulpaName) => {
      // agent_program_status from Arx — authoritative source.
      // Directly set state, no translation.
      const tulpa = tulpaName || getTulpaForPhase(activeDeck?.phase || 'gather');
      const validStates: AgentState[] = ['booting', 'idle', 'active', 'suspended'];
      if (validStates.includes(programStatus as AgentState)) {
        setAgentStatuses(prev => ({ ...prev, [tulpa]: programStatus as AgentState }));
        if (programStatus === 'idle') {
          onAgentReady(); // Dismiss warmup overlay
        }
      }
    },
    onArtifactSynced: (_phase, key, data) => {
      if (!activeDeck) return;
      if (key === 'storyboard' && data) {
        // Parse storyboard data — could be JSON or markdown
        const raw = typeof data === 'string' ? data : JSON.stringify(data);
        try {
          const sb = JSON.parse(raw);
          if (sb.cards && Array.isArray(sb.cards)) {
            // Convert JSON cards to markdown for the editor
            let md = '';
            if (sb.audience) md += `**Audience:** ${extractAudienceLabel(sb.audience)}\n\n`;
            if (sb.narrative) md += `**Narrative:** ${sb.narrative}\n\n---\n\n`;
            sb.cards.forEach((c: any, i: number) => {
              md += `## Slide ${i + 1}${c.tag ? ` [${c.tag}]` : ''}: ${c.title || ''}\n\n`;
              if (c.body) md += `${c.body}\n\n`;
            });
            setPlanMarkdown(md.trim());
            if (sb.audience) setStoryAudience(extractAudienceLabel(sb.audience));
            if (sb.narrative) setStoryNarrative(sb.narrative);
          } else {
            setPlanMarkdown(raw);
          }
        } catch {
          // Not JSON — treat as markdown directly
          setPlanMarkdown(raw);
        }

      }
      // Progressive preview: refresh iframe when any HTML artifact is updated
      if (key && key.endsWith('.html') && (key.startsWith('artifacts/') || key.startsWith('context/'))) {
        setActiveArtifactKey(key);
        setPreviewRefreshKey(prev => prev + 1);
        if (activeDeck) {
          api.getArtifacts(activeDeck.id).then(setArtifacts).catch(() => {});
        }
        // Auto-navigate to Preview when HTML exists.
        // Only block from gather phase (no brief yet). Plan phase can advance if slides exist.
        if (activeDeck && activeDeck.phase !== 'gather') {
          setMaxPhaseReached(prev => phaseIdx('preview') > phaseIdx(prev) ? 'preview' : prev);
          if (activeDeck.phase !== 'preview') {
            api.advancePhase(activeDeck.id, 'preview')
              .then(u => setActiveDeck(u))
              .catch(() => {});
          }
        }
      }
    },
    onPhaseChanged: (_from, to) => {
      if (activeDeck) {
        const toPhase = (to === 'generate' ? 'preview' : to) as Phase;
        setActiveDeck({ ...activeDeck, phase: toPhase });
        setMaxPhaseReached(prev => phaseIdx(toPhase) > phaseIdx(prev) ? toPhase : prev);
        refreshDecks();
      }
    },
    onContextUpdated: (action, key, actor) => {
      // Toggle events are handled optimistically — no need to re-fetch
      if (action === 'toggled') return;
      // Skip own edits — we already have the latest locally (optimistic update)
      const isSelf = actor === `user:${user?.username}`;
      if (activeDeck) {
        api.listContext(activeDeck.id).then(ctx => {
          setContextItems(ctx);
          // Sync storyboard from remote update (other user or agent) — skip own edits
          if (key === 'memory/storyboard.md' && !isSelf) {
            const storyboardCtx = ctx.find(c => c.key === 'memory/storyboard.md');
            if (storyboardCtx?.content) {
              if (planDirtyRef.current) {
                setPendingAgentPlan(storyboardCtx.content);
              } else {
                setPlanMarkdown(storyboardCtx.content);
              }
            }
          }
          // Sync speaker notes
          if (key === 'memory/speaker-script.md' && !isSelf) {
            const notesCtx = ctx.find(c => c.key === 'memory/speaker-script.md');
            if (notesCtx?.content) {
              setNotesMarkdown(notesCtx.content);
            }
          }
        }).catch(() => {});
      }
      // Notify ConceptCloud when pitch-brief is updated (e.g. by agent)
      if (key === 'memory/pitch-brief.json' || key === 'memory/pitch-brief') {
        setConceptCloudVersion(prev => prev + 1);
      }
      // No need to refetch Confluence pages on context_updated —
      // reference/remove handlers already do optimistic updates.
    },
    onDeckUpdated: (fields) => {
      if (activeDeck) {
        api.getDeck(activeDeck.id).then(setActiveDeck).catch(() => {});
        refreshDecks();
      }
      // Refresh concept cloud when title changes (might indicate new content)
      if (fields?.includes('title')) {
        setConceptCloudVersion(prev => prev + 1);
      }
    },
    onTaskStatus: (task) => {
      // Parse "target:action" or "target:action:done"
      const parts = task.split(':');
      const target = parts[0]; // 'plan', 'notes', or 'preview'
      const action = parts[1] as 'create' | 'update';
      const isDone = parts[2] === 'done';
      if (target === 'plan') {
        setPlanTask(isDone ? null : action);
      } else if (target === 'notes') {
        setNotesTask(isDone ? null : action);
      } else if (target === 'preview') {
        setPreviewTask(isDone ? null : action);
      } else if (target === 'presentation') {
        // New presentation version materialised by the agent, or
        // active version flipped — bump the preview refresh key so
        // the iframe + version dropdown re-fetch.
        setPreviewRefreshKey(prev => prev + 1);
        if (activeDeck) {
          const deckId = activeDeck.id;
          api.getArtifacts(deckId).then(setArtifacts).catch(() => {});
          api.getDeck(deckId).then(updated => {
            if (activeDeckIdRef.current !== deckId) return;
            setActiveDeck(updated);
            setMaxPhaseReached(prev => phaseIdx('preview') > phaseIdx(prev) ? 'preview' : prev);
          }).catch(() => {});
        }
      }
    },
    onSlideProgress: (slide, _total, _deckFolder) => {
      setActiveSlideHash(`#slide-${String(slide).padStart(2, '0')}`);
    },
  };

  // ── Stream Architecture: one deck timeline SSE channel ──
  // Feature flag: enable once Arx broadcast transport is deployed

  // Per-agent accumulated text for the stream hooks
  const streamTextsRef = useRef<Record<string, string>>({});

  // Mirrors `messages.length` synchronously so onConnected's REST handler can
  // compute the "N new messages arrived during downtime" delta without a stale
  // closure over the state variable.
  const messagesLengthRef = useRef(0);
  useEffect(() => { messagesLengthRef.current = messages.length; }, [messages.length]);

  // Stale-activity reconciler — port of PP PR #73. Without it the
  // "Agent is working" overlay locks on forever whenever the SSE
  // :done event is lost (tab backgrounded, network drop, Worker
  // isolate gc'd mid-stream).
  //
  // Server-authoritative fix: GET /api/decks/:id now returns
  // `signals.is_actively_working` derived from turn_state plus the
  // same 60s TTL that tryStartTurn uses to auto-recover a stuck lock.
  // Reconcile local state against it whenever activeDeck refreshes.
  //
  // Skip the clear while a turn is mid-stream locally — onMessage may
  // have fired before the server saw the task start. Otherwise we'd
  // flicker the indicator off→on on the user's first send.
  useEffect(() => {
    const working = activeDeck?.signals?.is_actively_working;
    // Three states to handle (aligns PP main #130 / 2.34.19):
    //   true  — server says agent is working; lift local flag if it
    //           was cleared by a missed `done` or a fresh page load.
    //           Without this, the user sees no working bubble even
    //           though the agent is actively producing output (SSE
    //           reconnect didn't re-arm the bubble).
    //   false — server says no in-flight task; clear local flag if
    //           no stream is currently arriving (guard against
    //           clearing mid-turn when the server hasn't recorded
    //           the lock yet).
    //   undefined — legacy server build with no signals; leave local
    //               state alone.
    if (working === true || (working === undefined && activeDeck?.active_task)) {
      setIsGeneratingRaw(true);
      return;
    }
    if (working !== false) return;
    if (Object.keys(streamTextsRef.current).length > 0) return;
    setIsGeneratingRaw(false);
    setActivityItems([]);
    setPlanTask(null);
    setNotesTask(null);
    setPreviewTask(null);
  }, [activeDeck?.signals?.is_actively_working]);

  // Tab-visibility refresher — when user switches back to a deck after
  // being on another tab, fetch a fresh deck record so signals (and
  // the reconciler above) reflect ground truth.
  useEffect(() => {
    if (typeof document === 'undefined') return;
    const onVisible = () => {
      if (document.visibilityState !== 'visible') return;
      const deckId = activeDeck?.id;
      if (!deckId) return;
      api.getDeck(deckId)
        .then(d => {
          if (activeDeck && activeDeck.id !== d.id) return;
          setActiveDeck(d);
        })
        .catch(() => { /* transient; reconciler runs on next refresh */ });
    };
    document.addEventListener('visibilitychange', onVisible);
    return () => document.removeEventListener('visibilitychange', onVisible);
  }, [activeDeck?.id]);

  // Network-level online/offline listeners — distinguishes "SSE dropped" from
  // "whole internet is down" so the banner can show the right mental model.
  useEffect(() => {
    if (typeof window === 'undefined') return;
    const goOnline = () => setIsOnline(true);
    const goOffline = () => setIsOnline(false);
    window.addEventListener('online', goOnline);
    window.addEventListener('offline', goOffline);
    return () => {
      window.removeEventListener('online', goOnline);
      window.removeEventListener('offline', goOffline);
    };
  }, []);

  // 1s ticker — only runs while we need live threshold evaluation (disconnected
  // or showing the back-online fade). No cost when the connection is healthy.
  useEffect(() => {
    if (disconnectedAt === null && backOnlineUntil === null) return;
    const int = setInterval(() => setNowTick(Date.now()), 1000);
    return () => clearInterval(int);
  }, [disconnectedAt, backOnlineUntil]);

  // P1: flip the ChatPanel generating indicator to the "Connection lost" variant
  // when (a) a turn is actively buffering text in streamTextsRef and (b) the
  // client-side stream has been dropped for ≥10s. Sub-threshold blips stay silent.
  const streamingStalled =
    disconnectedAt !== null &&
    (nowTick - disconnectedAt) >= 10_000 &&
    Object.keys(streamTextsRef.current).length > 0;

  // Force a retry from the lost-long banner — bump chatReconnectKey, which
  // deckChat's useEffect depends on, causing it to tear down and reconnect.
  // MUST live above any early returns to satisfy the Rules of Hooks.
  const handleManualReconnect = useCallback(() => {
    setChatReconnectKey(k => k + 1);
  }, []);

  // Scroll the chat list to the bottom when the user clicks the "N new messages"
  // banner. Dispatched as a CustomEvent so App.tsx doesn't have to thread a ref
  // through ChatPanel.
  const handleScrollToNewMessages = useCallback(() => {
    setNewMessagesCount(0);
    if (typeof window !== 'undefined') {
      window.dispatchEvent(new CustomEvent('pp:scroll-chat-to-bottom'));
    }
  }, []);

  // Derive ConnectionStatus for the banner.
  // Priority: offline > lost-long (≥30s) > lost (≥10s) > reconnecting (≥3s)
  //         > back-online (recent transition) > connected.
  //
  // Error count is deliberately NOT a direct escalation signal — with a 1s / 2s
  // / 4s retry backoff the counter hits 3 in ~4 seconds, which is far too fast
  // to justify the scariest banner. How long the user has been disconnected is
  // the right measure for alarm level.
  const connectionStatus: ConnectionStatus = (() => {
    if (!isOnline) return 'offline';
    if (disconnectedAt !== null) {
      const elapsed = nowTick - disconnectedAt;
      if (elapsed >= 30_000) return 'lost-long';
      if (elapsed >= 10_000) return 'lost';
      if (elapsed >= 3_000) return 'reconnecting';
      // <3s: stay silent; ticker will promote us past threshold when we cross it.
      return 'connected';
    }
    if (backOnlineUntil !== null && nowTick < backOnlineUntil) return 'back-online';
    return 'connected';
  })();

  // Bumped by the manual-reconnect button to force useDeckTimeline to tear
  // down its current subscription and re-open the deck event stream.
  const [chatReconnectKey, setChatReconnectKey] = useState(0);

  const handleTimelineEvent = useCallback((event: AgentTimelineEvent) => {
    const visibleEvent =
      event.type !== 'message.user' &&
      (event.meta?.visibility ?? 'chat') === 'chat' &&
      !!event.turnId;
    setAgentTimelineItems(prev => {
      const next = applyAgentTimelineEvent(prev, event);
      if (!visibleEvent) return next;
      const hasRenderableSuccessor = next.some(item => {
        const raw = item.raw && typeof item.raw === 'object' ? item.raw as { localQueued?: boolean } : null;
        return item.turnId === event.turnId && !raw?.localQueued && item.kind !== 'chat.user';
      });
      if (!hasRenderableSuccessor) return next;
      return next.filter(item => {
        const raw = item.raw && typeof item.raw === 'object' ? item.raw as { localQueued?: boolean } : null;
        return !(raw?.localQueued && item.turnId === event.turnId);
      });
    });
    if (event.type !== 'message.assistant.file') return;
    if (
      event.fileUrl
      && (event.mime?.split(';', 1)[0]?.trim().toLowerCase() === 'text/html' || /\.html?$/i.test(event.name))
    ) {
      setArtifactView({
        filename: event.name,
        fileMime: event.mime || 'text/html',
        fileSize: event.size,
        fileUrl: event.fileUrl,
      });
      setPanelOpen(true);
    }
    const id = `file-${event.rowId || `${event.turnId}-${event.name}-${event.ts}`}`;
    setMessages(prev => {
      if (prev.some(m => m.id === id)) return prev;
      return [...prev, {
        id,
        role: 'assistant',
        content: event.name,
        displayContent: event.name,
        messageType: 'file',
        fileHash: event.fileHash,
        fileKey: event.fileKey,
        fileUrl: event.fileUrl,
        fileMime: event.mime,
        fileSize: event.size,
        timestamp: event.ts,
        agent: event.agent,
        agentId: event.agentId,
      }];
    });
  }, []);

  // SSE always connected when deck is active (PP backend handles agent-not-ready with 'waiting' + quick reconnect)
  useDeckTimeline(activeDeck?.id || null, {
    ...deckTimelineEventHandlers,
    onTimelineEvent: handleTimelineEvent,
    onConnected: (isReconnect) => {
      setChatConnected(true);
      // If we were in a visible-disconnect state (banner was showing), flash the
      // transient "Back online" confirmation for ~1.2s. For sub-3s blips we stay
      // silent — no visible transition to acknowledge.
      const wasVisiblyDisconnected =
        disconnectedAt !== null && (Date.now() - disconnectedAt) >= 3_000;
      if (wasVisiblyDisconnected) {
        setBackOnlineUntil(Date.now() + 1200);
      }
      // Reset connection-failure counters on successful connect.
      setDisconnectedAt(null);
      setChatErrorCount(0);

      // Pull messages via REST on RECONNECT to catch anything missed during the
      // disconnect. On the FIRST connect we skip this fetch — the deck-open
      // path (loadDeck / initial-load IIFE) already fetched messages, and the
      // <100ms gap to SSE subscribe is too short for new content to slip past
      // both the REST snapshot and the live stream. Avoids the long-standing
      // 2-3x duplicate /messages volley seen on every deck-open.
      if (activeDeck && isReconnect) {
        const prevLength = messagesLengthRef.current;
        api.getMessages(activeDeck.id, 50, 0).then(msgs => {
          if (!msgs || !Array.isArray(msgs)) return;
          const transformed = transformMessages([...msgs].reverse());
          // Merge, don't replace: if a turn is still streaming (streaming-* ids in
          // state), preserve those entries — the REST snapshot only contains
          // persisted messages, so wiping would erase the live bubble. onDone will
          // later convert streaming-* ids to persisted ids and the next REST
          // snapshot will include them.
          // Also preserve `local-*` optimistic user-message entries: when the user
          // sends a message and the SSE stream reconnects before the message is
          // persisted, the REST snapshot doesn't yet include that turn. Wiping it
          // would race with onUserMessage's map-by-localId swap and the user's
          // bubble would vanish until the next reload.
          setMessages(prev => {
            const inflight = prev.filter(m =>
              m.id?.startsWith?.('streaming-') || m.id?.startsWith?.('local-')
            );
            if (inflight.length === 0) return transformed;
            return [...transformed, ...inflight];
          });
          // Only clear indicators when no turn is actively buffering — the REST
          // array never contains streaming-* ids (those are frontend-only), so
          // the previous `transformed.some(...)` check was always-false here
          // and unconditionally reset isGenerating on every reconnect. Use the
          // existing stream buffer as the source of truth instead.
          if (Object.keys(streamTextsRef.current).length === 0) {
            setIsGenerating(false);
            clearActivity();
          }
          // After a visible-disconnect reconnect, detect messages the user missed.
          // Not shown for first-ever connect or sub-threshold blips.
          if (isReconnect && wasVisiblyDisconnected) {
            const delta = transformed.length - prevLength;
            if (delta > 0) setNewMessagesCount(delta);
          }
        }).catch(() => {});
      }
    },
    onStreamDropped: (consecutive) => {
      setChatConnected(false);
      setChatErrorCount(consecutive);
      // Mark the start of a disconnect streak; the threshold-driven banner uses
      // this to escalate from reconnecting (3s) → lost (10s) → lost-long (30s).
      setDisconnectedAt(prev => prev === null ? Date.now() : prev);
    },
    onContent: (text, agent, turnId, agentId) => {
      const streamId = turnId ? `streaming-${turnId}` : (agent ? `streaming-${agent}` : 'streaming-default');
      if (!streamTextsRef.current[streamId]) streamTextsRef.current[streamId] = '';
      // Filter raw stream-json protocol lines
      const delta = text.split('\n').filter((line: string) => {
        const trimmed = line.trim();
        if (!trimmed) return true;
        if (trimmed.startsWith('{"type":"')) return false;
        return true;
      }).join('\n').replace(/\[TASK:[^\]]*\]/gi, '');
      if (!delta) return;

      streamTextsRef.current[streamId] += delta;
      const accumulated = streamTextsRef.current[streamId];

      setMessages(prev => {
        const idx = prev.findIndex(m => m.id === streamId);
        if (idx >= 0) {
          const updated = [...prev];
          // Latch agentId on first non-empty value — Arx emits it on
          // every content delta; we only need the chip target once.
          updated[idx] = {
            ...updated[idx],
            content: accumulated,
            agentId: updated[idx].agentId || agentId || undefined,
          };
          return updated;
        }
        return [...prev, {
          id: streamId,
          role: 'assistant',
          content: accumulated,
          timestamp: Date.now(),
          agent,
          agentId: agentId || undefined,
        }];
      });
      setIsGenerating(true);
    },
    onDone: (event) => {
      const doneTurnId = (event.turn_id as string) || '';
      // Finalize streaming messages for this turn
      setMessages(prev => prev
        .filter(m => !(m.id.startsWith('streaming-') && m.content.trim().startsWith('[SILENT]')))
        .map(m => {
          if (m.id.startsWith('streaming-') && (!doneTurnId || m.id === `streaming-${doneTurnId}`)) {
            return { ...m, id: `msg-${Date.now()}-${doneTurnId || m.agent || 'unknown'}` };
          }
          return m;
        })
      );
      // Always clear generating state on done
      setIsGenerating(false);
      clearActivity();
      // Clean up stream buffer for this turn
      if (doneTurnId && streamTextsRef.current[`streaming-${doneTurnId}`]) {
        delete streamTextsRef.current[`streaming-${doneTurnId}`];
      } else {
        streamTextsRef.current = {};
      }
      // Refresh deck data
      refreshDecks();
      if (activeDeck) {
        api.getDeck(activeDeck.id).then(updated => {
          if (activeDeckIdRef.current !== activeDeck.id) return;
          setActiveDeck(updated);
          loadDeckData(updated);
        }).catch(() => {});
      }
    },
    onError: (message, agent) => {
      setMessages(prev => [...prev, {
        id: `err-${Date.now()}`, role: 'system',
        content: `${agent ? `[${agent}] ` : ''}${message}`,
        timestamp: Date.now(),
      }]);
    },
    onUserMessage: (messageId, content, _files, attachments) => {
      // Server is the source of truth — render user messages from SSE broadcast.
      // All tabs (including sender) receive this and render uniformly.
      setMessages(prev => {
        // Dedup by message_id (SSE may re-deliver on reconnect)
        if (prev.some(m => m.id === messageId)) return prev;
        // If this is OUR own just-sent message, replace the optimistic "local-…"
        // entry with the server-assigned id so future dedup works across reloads.
        const pending = pendingLocalMessageRef.current;
        if (pending && pending.content === content) {
          pendingLocalMessageRef.current = null;
          // Defensive: if the anchor optimistic entry is no longer present
          // (e.g. it was wiped by a REST-snapshot merge during an SSE
          // reconnect race), append a fresh entry instead of silently
          // no-op'ing. Otherwise re-key the anchor AND any sibling
          // optimistic entries (`<localId>-ref-N`, `<localId>-text`
          // that sendMessage emits for attachment sends) so a later
          // REST refetch dedups them against the persisted-row
          // expansion via their `messageId-*` ids (aligns PP main #114).
          const hasLocal = prev.some(m => m.id === pending.localId);
          const realId = messageId || `usr-${Date.now()}`;
          if (hasLocal) {
            return prev.map(m => {
              if (m.id === pending.localId) {
                return { ...m, id: realId, attachments: attachments ?? m.attachments };
              }
              if (m.id.startsWith(`${pending.localId}-`)) {
                return { ...m, id: `${realId}${m.id.slice(pending.localId.length)}` };
              }
              return m;
            });
          }
          return [...prev, { id: realId, role: 'user', content, timestamp: Date.now(), attachments }];
        }
        return [...prev, { id: messageId || `usr-${Date.now()}`, role: 'user', content, timestamp: Date.now(), attachments }];
      });
    },
    onSessionLimit: () => {
      setMessages(prev => {
        if (prev.some(m => m.id === 'session-limit')) return prev;
        return [...prev, {
          id: 'session-limit', role: 'system',
          content: 'Too many active sessions. Close a tab or device to continue.',
          timestamp: Date.now(),
        }];
      });
    },
    onActivity: ({ phase, tool, content, agent, agentId, turnId, ts: eventTs }) => {
      const resolvedTurnId = turnId || activeActivityTurnIdRef.current || pendingLocalActivityTurnIdRef.current || '';
      if (turnId) bindLocalActivityTurn(turnId, agent, agentId);
      if (resolvedTurnId) activeActivityTurnIdRef.current = resolvedTurnId;
      // Append-or-create: every activity item belongs to a turn (keyed
      // by `turnId`). New turn → push a new live `TurnRecord`; existing
      // turn → mutate its items[].
      //
      // `turns` is wiped only by `clearActivity()` (deck switch / reset)
      // and per-turn status flips to 'done' on the explicit `done` event.
      // The `talking`/`result` phase below clears the legacy flat
      // activityItems but DOES NOT clear `turns` — the live turn keeps
      // showing its faded done items until the bridge emits `done`.
      const appendToTurn = (item: ActivityItem) => {
        if (!item.turnId) return;
        setTurns(prev => {
          const idx = prev.findIndex(t => t.turnId === item.turnId);
          if (idx >= 0) {
            const next = [...prev];
            next[idx] = { ...next[idx], items: [...next[idx].items, item] };
            return next;
          }
          return [...prev, {
            turnId,
            agent,
            agentId,
            items: [item],
            status: 'live',
            startedAt: item.ts,
          }];
        });
      };

      const updateLastRunningInTurn = (mutator: (i: ActivityItem) => ActivityItem) => {
        if (!resolvedTurnId) return;
        setTurns(prev => {
          const idx = prev.findIndex(t => t.turnId === resolvedTurnId);
          if (idx < 0) return prev;
          const turn = prev[idx];
          const last = [...turn.items].reverse().findIndex(i => i.status === 'running');
          if (last < 0) return prev;
          const realIdx = turn.items.length - 1 - last;
          const next = [...prev];
          const items = [...turn.items];
          items[realIdx] = mutator(items[realIdx]);
          next[idx] = { ...turn, items };
          return next;
        });
      };

      switch (phase) {
        case 'started':
        case 'thinking':
        case 'building':
        case 'writing':
        case 'error':
        case 'tool_use': {
          setIsGenerating(true);
          const detail = extractDetail(tool, content);
          const item: ActivityItem = {
            id: `act-${Date.now()}-${Math.random().toString(36).slice(2, 6)}`,
            tool,
            detail,
            status: phase === 'error' ? 'done' : 'running',
            // Use the source event's wall-clock so reloaded history
            // turns sort into the chat panel at their original
            // position. On live SSE this is "now"; on
            // /activity-history replay this is the recovered
            // created_at (#115).
            ts: eventTs,
            turnId: resolvedTurnId,
            agentId,
          };
          // Maintain the legacy flat list for components still consuming it.
          setActivityItems(prev => item.turnId ? [...prev, item] : prev);
          appendToTurn(item);
          if (phase === 'error') setActivityError(true);
          else setActivityError(false);
          break;
        }
        case 'tool_result': {
          const resultDetail = content ? content.slice(0, 500) : '';
          const apply = (i: ActivityItem): ActivityItem => ({
            ...i,
            status: 'done',
            detail: i.detail + (resultDetail ? '\n→ ' + resultDetail : ''),
          });
          setActivityItems(prev => {
            const items = [...prev];
            const last = items.findLastIndex(i => i.status === 'running');
            if (last >= 0) items[last] = apply(items[last]);
            return items;
          });
          updateLastRunningInTurn(apply);
          break;
        }
        case 'streaming':
          setIsGenerating(true);
          setActivityError(false);
          break;
        case 'talking':
        case 'result':
          // Legacy clear path — preserved for components reading the
          // flat list. The grouped `turns` view is updated on `done`.
          setActivityItems([]);
          break;
      }
    },
    onActivityDone: ({ turnId, summary, durationMs }) => {
      if (activeActivityTurnIdRef.current === turnId) {
        activeActivityTurnIdRef.current = null;
      }
      if (pendingLocalActivityTurnIdRef.current === turnId) {
        pendingLocalActivityTurnIdRef.current = null;
      }
      // Mark the matching turn complete + stash summary so the renderer
      // can collapse it. Bridge guarantees one `done` per turn.
      setTurns(prev => {
        const idx = prev.findIndex(t => t.turnId === turnId);
        if (idx < 0) return prev;
        const next = [...prev];
        next[idx] = {
          ...next[idx],
          status: 'done',
          summary,
          durationMs,
          // Any item still flagged 'running' at done-time gets demoted —
          // the bridge dropped the matching tool_result (rare; engine
          // cancellation, network glitch). Better to fade it than show
          // a perpetual spinner.
          items: next[idx].items.map(i => i.status === 'running' ? { ...i, status: 'done' as const } : i),
        };
        return next;
      });
    },
    // History-load reconciler. The activity-history backfill replays
    // PAST events through onActivity, which re-arms isGenerating + can
    // leave a 'live' turn (the "Building (N)" bubble) hanging when the
    // original turn's `done` was missed (tab closed mid-turn, SSE drop).
    // Once the backfill finishes, no turn can legitimately still be live
    // unless a real local stream is in flight right now. If none is,
    // clear the generating flag + finalize any lingering 'live' turn so
    // reopening an idle deck doesn't show a stuck "Building" indicator.
    onHistoryLoaded: () => {
      if (Object.keys(streamTextsRef.current).length > 0) return;
      setIsGeneratingRaw(false);
      setTurns(prev => prev.some(t => t.status === 'live')
        ? prev.map(t => t.status === 'live'
            ? {
                ...t,
                status: 'done' as const,
                items: t.items.map(i => i.status === 'running' ? { ...i, status: 'done' as const } : i),
              }
            : t)
        : prev);
    },
  }, chatReconnectKey);

  // Reset agent status when switching decks and keep ref in sync
  useEffect(() => {
    activeDeckIdRef.current = activeDeck?.id || null;
    setAgentStatuses({});
  }, [activeDeck?.id]);

  // Resume data ref — kept for context loading, no auto-greeting
  const resumeDataRef = useRef<{ deck: Deck; ctx: api.ContextItem[]; arts: api.ArtifactItem[]; msgs: ChatMessage[] } | null>(null);

  const refreshDecks = useCallback(async () => {
    try {
      if (activeWorkspaceId) {
        const d = await api.listWorkspaceDecks(activeWorkspaceId);
        setDecks(d);
      } else {
        // No workspace selected — should not happen in normal flow
        const d = await api.listDecks();
        setDecks(d);
      }
    } catch {}
  }, [activeWorkspaceId]);

  const handleSelectWorkspace = useCallback(async (wsId: string) => {
    setActiveWorkspaceId(wsId);
    setDecks([]);    // Clear stale decks
    navigate(`/w/${wsId}`);
    try {
      const d = await api.listWorkspaceDecks(wsId);
      setDecks(d);
    } catch {}
  }, []);

  const selectDeck = useCallback(async (id: string, prefetched?: Deck, opts?: { skipNavigate?: boolean }) => {
    // Invalidate stale SSE callbacks BEFORE aborting (so error handlers are blocked)
    activeDeckIdRef.current = id;
    setOpeningDeckId(id);
    // Clear ALL old deck state immediately (before async fetch)
    setIsGenerating(false);
    clearActivity();
    setMessages([]);
    setAgentTimelineItems([]);
    setPlanMarkdown('');
    setNotesMarkdown('');
    setStoryAudience('');
    setStoryNarrative('');
    setContextItems([]);
    setContextHydratedDeckId(null);
    setArtifacts([]);
    setPanelOpen(false);
    setUserDragged(false);
    setPlanTask(null);
    setNotesTask(null);
    setPreviewTask(null);
    setPlanVersions([]);
    setNotesVersions([]);
    setActivePlanKey('memory/storyboard.md');
    setActiveNotesKey('memory/speaker-script.md');
    setActiveArtifactKey(null);
    setEditMode(null);
    setViewerFile(null);
    setDeckMembers([]);
    setPendingToggleKeys(new Set());
    setMaxPhaseReached('gather');
    setChatConnected(false);
    setAgentStatuses({});
    setIsWarmingUp(false);

    try {
      // Use the pre-fetched deck when the caller already has one (e.g. straight
      // out of createDeck) — saves a redundant GET /api/decks/:id on the
      // critical path of "click New → enter deck".
      const deck = prefetched ?? await api.getDeck(id);
      setActiveDeck(deck);
      setMaxPhaseReached(computePanelPhase(deck, [], []));
      // skipNavigate when invoked from the back/forward route effect —
      // popstate already updated the URL, so pushing again would add a
      // duplicate history entry.
      if (!opts?.skipNavigate) navigate(`/deck/${id}`);

      // A prefetched deck (from the deck list or straight out of
      // createDeck) carries no computed `signals` — the list endpoint
      // doesn't run the artifact-grouper. Without artifacts +
      // active_artifact_id the Preview panel never mounts, so an
      // already-built deck opens to chat-only. Reconcile with the full
      // GET /decks/:id record (which includes signals) in the
      // background; guard against a fast deck-switch clobbering state.
      if (prefetched) {
        void api
          .getDeck(id)
          .then((full) => {
            if (activeDeckIdRef.current === id) {
              setActiveDeck(full);
              setMaxPhaseReached(computePanelPhase(full, [], []));
            }
          })
          .catch(() => {
            /* transient; visibility-change + other refreshers retry */
          });
      }

      // Start warmup IN PARALLEL with data loading — don't wait for messages/context first.
      // This makes the overlay dismiss the moment both warmup and data are ready, instead
      // of serializing 5+ API calls before even starting the agent.
      const warmupPromise = warmupAndReady(id);

      // Restore active task state from persisted deck data
      if (deck.active_task) {
        const parts = deck.active_task.split(':');
        const target = parts[0];
        const action = parts[1] as 'create' | 'update';
        if (target === 'plan') setPlanTask(action);
        else if (target === 'notes') setNotesTask(action);
        else if (target === 'preview') setPreviewTask(action);
        setIsGenerating(true);
        clearActivity();
      } else {
        setPlanTask(null);
        setNotesTask(null);
        setPreviewTask(null);
      }
      const PAGE_SIZE = 50;
      const [msgs, ctx, arts] = await Promise.all([
        api.getMessages(id, PAGE_SIZE, 0),
        api.listContext(deck.id).catch(() => [] as api.ContextItem[]),
        api.getArtifacts(deck.id).catch(() => [] as api.ArtifactItem[]),
      ]);
      const chronological = Array.isArray(msgs) ? [...msgs].reverse() : [];
      const transformedMsgs = transformMessages(chronological);
      setMessages(transformedMsgs);
      setHasMoreMessages(msgs.length >= PAGE_SIZE);
      setMessagesLoaded({ deckId: id, count: transformedMsgs.length });
      // Signal to ConceptCloud that the parent's context snapshot is
      // ready for THIS deck — see contextHydratedDeckId comment at the
      // useState declaration.
      setContextHydratedDeckId(id);
      // Pass ctx + arts in so loadDeckData doesn't re-fetch them — the
      // Promise.all above already paid that cost.
      await loadDeckData(deck, { ctx, arts });
      resumeDataRef.current = { deck, ctx, arts, msgs: transformedMsgs };

      void warmupPromise.catch(() => {});
    } catch {
      setIsWarmingUp(false);
    } finally {
      setOpeningDeckId(null);
    }
  }, [loadDeckData]);

  // Keep a stable ref to selectDeck so the back/forward route effect
  // (defined earlier in this component) can reuse its full clear+load
  // flow without a forward-reference dependency / TDZ.
  selectDeckRef.current = selectDeck;

  const loadMoreMessages = useCallback(async () => {
    if (!activeDeck || loadingMoreMessages || !hasMoreMessages) return;
    setLoadingMoreMessages(true);
    try {
      const PAGE_SIZE = 50;
      const offset = messages.length;
      const older = await api.getMessages(activeDeck.id, PAGE_SIZE, offset);
      // arx returns newest first — reverse and these are older messages
      const expanded = transformMessages([...older].reverse());
      // Deduplicate by id before prepending
      setMessages(prev => {
        const existingIds = new Set(prev.map(m => m.id));
        const newMsgs = expanded.filter((m: ChatMessage) => !existingIds.has(m.id));
        return [...newMsgs, ...prev];
      });
      setHasMoreMessages(older.length >= PAGE_SIZE);
    } catch (err) {
      console.error('Failed to load more messages:', err);
    } finally {
      setLoadingMoreMessages(false);
    }
  }, [activeDeck, loadingMoreMessages, hasMoreMessages, messages.length]);

  const createDeck = useCallback(async (opts?: { title?: string; narrative?: string; style?: string; templateRef?: string; folder_id?: string }): Promise<Deck | void> => {
    setIsCreating(true);
    const isTemplate = !!opts?.templateRef;
    try {
      const deck = await api.createDeck(opts?.title, opts?.narrative, opts?.style, opts?.templateRef, opts?.folder_id, activeWorkspaceId || undefined);
      // POST already returned the full Deck — skip the GET /workspaces/:wsId/decks
      // round-trip on the hot path and just splice the new deck into local state.
      // The full list refresh stays available via refreshDecks() if anything
      // ever goes out of sync (e.g. multi-tab edit).
      setDecks(prev => {
        if (prev.some(d => d.id === deck.id)) return prev;
        return [deck, ...prev];
      });

      // Templates: navigate into deck immediately
      // Regular decks: stay on dashboard so user sees it in the list
      if (!isTemplate) {
        setIsCreating(false);
        return deck;
      }

      setActiveDeck(deck);
      setPlanMarkdown('');
      setStoryAudience('');
      setStoryNarrative('');
      setContextItems([]);
      setContextHydratedDeckId(null);
      setArtifacts([]);
      setPanelOpen(false);
      setUserDragged(false);
      setAgentStatuses({});
      setIsWarmingUp(false);
      navigate(`/deck/${deck.id}`);

      if (isTemplate) {
        // Template deck: keep warmup overlay throughout the entire flow
        // Don't show intermediate file card messages — reveal everything at once
        setMessages([]);
        setAgentTimelineItems([]);
        setMaxPhaseReached('plan');

        // Warm up the agent in the background while template data loads.
        void warmupAndReady(deck.id);

        // Now load all template data (plan markdown, context, artifacts)
        await loadDeckData(deck);
        setContextHydratedDeckId(deck.id);

        // Reveal everything at once: messages, phase, preview
        const tplMessage = "[REF:file] reference-storyboard.md\n[REF:file] reference-deck.html\nI've loaded two reference files — a storyboard and a preview deck — as a starting point. I'll share the details of what I need next.";
        const now = Date.now();
        setMessages(expandRefMessage({
          id: `tpl-${now}`, role: 'user', content: tplMessage, timestamp: now,
        }));
        setMaxPhaseReached('preview');
        setPreviewRefreshKey(1);

        // Send the combined message to agents (skip local — already added above)
        // This persists to Arx so file cards survive refresh
        setTimeout(() => {
          sendMessageRef.current?.(
            "[REF:file] reference-storyboard.md\n[REF:file] reference-deck.html\nI've loaded two reference files — a storyboard and a preview deck — as a starting point. I'll share the details of what I need next.",
            { skipLocalMessage: true }
          );
        }, 500);
      } else {
        setMessages([]);
        setAgentTimelineItems([]);
        setMaxPhaseReached('plan');
        setMessagesLoaded({ deckId: deck.id, count: 0 });

        // Warm up the agent in the background on new deck creation.
        void warmupAndReady(deck.id);
      }
    } catch {
      setIsWarmingUp(false);
    }
    setIsCreating(false);
  }, [activeWorkspaceId, loadDeckData, warmupAndReady]);

  // Manual save for plan markdown (triggered by Save button, not auto)
  const handlePlanMarkdownChange = useCallback((md: string) => {
    setPlanMarkdown(md);
    planDirtyRef.current = true;
  }, []);

  const handlePlanSave = useCallback((md: string) => {
    if (!activeDeck) return;
    setPlanSaveStatus('saving');
    api.saveContext(activeDeck.id, activePlanKey, md)
      .then(() => {
        setPlanSaveStatus('saved');
        setTimeout(() => setPlanSaveStatus(s => s === 'saved' ? 'idle' : s), 2000);
      })
      .catch(() => setPlanSaveStatus('idle'));
  }, [activeDeck, activePlanKey]);

  // Manual save for notes markdown
  const handleNotesChange = useCallback((md: string) => {
    setNotesMarkdown(md);
  }, []);

  const handleNotesSave = useCallback((md: string) => {
    if (!activeDeck) return;
    setNotesSaveStatus('saving');
    api.saveContext(activeDeck.id, activeNotesKey, md)
      .then(() => {
        setNotesSaveStatus('saved');
        setTimeout(() => setNotesSaveStatus(s => s === 'saved' ? 'idle' : s), 2000);
      })
      .catch(() => setNotesSaveStatus('idle'));
  }, [activeDeck, activeNotesKey]);

  const advancePhase = useCallback(async (phase: Phase) => {
    if (!activeDeck) return;
    try {
      // If backend already at this phase, just refresh
      let updated;
      if (activeDeck.phase === phase) {
        updated = await api.getDeck(activeDeck.id);
      } else {
        updated = await api.advancePhase(activeDeck.id, phase);
      }
      setActiveDeck(updated);
      setMaxPhaseReached(prev => phaseIdx(phase) > phaseIdx(prev) ? phase : prev);
      await refreshDecks();

      // Trigger builder when advancing to preview (user clicked "Approve & Build")
      if (phase === 'preview' && !isGenerating) {
        setTimeout(() => sendMessage('build'), 300);
      }
    } catch (err: any) {
      // Ignore "already at this phase" errors (backend already transitioned)
      if (err.message?.includes('Cannot transition')) return;
      setMessages(prev => [...prev, {
        id: `err-${Date.now()}`,
        role: 'system',
        content: err.message || 'Phase transition failed',
        timestamp: Date.now(),
      }]);
    }
  }, [activeDeck, refreshDecks, isGenerating]);

  const handleUploadFile = useCallback(async (file: UploadedFile, onProgress?: (pct: number) => void) => {
    if (!activeDeck) return;
    if (file.rawFile) {
      // Multipart upload via XHR so the byte-level progress event can
      // stream back to the composer's progress bar. fetch() cannot
      // surface upload progress reliably in any browser today.
      await api.uploadContextFile(activeDeck.id, file.rawFile, undefined, undefined, (loaded, total) => {
        if (onProgress && total > 0) onProgress(Math.round((loaded / total) * 100));
      });
    } else {
      // Fallback to JSON save for text-only content without rawFile
      await api.saveContext(activeDeck.id, file.name, file.content, file.contentType);
    }
    // Refresh context items
    const ctx = await api.listContext(activeDeck.id).catch(() => []);
    setContextItems(ctx);
  }, [activeDeck]);

  const handleToggleFile = useCallback(async (key: string, enable: boolean) => {
    if (!activeDeck) return;
    setPendingToggleKeys(prev => new Set(prev).add(key));
    try {
      await api.toggleContext(activeDeck.id, key, enable);
      // Optimistically update enabled state locally
      setContextItems(prev => prev.map(c =>
        c.key === key ? { ...c, enabled: enable ? 1 : 0 } : c
      ));
    } catch {} finally {
      setPendingToggleKeys(prev => {
        const next = new Set(prev);
        next.delete(key);
        return next;
      });
    }
  }, [activeDeck]);

  // ─── Droploft handlers ───

  const fetchDroploftConnections = useCallback(async () => {
    try {
      const conns = await api.listDroploftConnections();
      setDroploftConnections(conns);
      return conns;
    } catch {
      setDroploftConnections([]);
      return [];
    }
  }, []);

  const fetchDroploftDocuments = useCallback(
    async (deckId: string, query?: string, connectionId?: string) => {
      setDroploftLoading(true);
      try {
        const docs = await api.searchDroploftDocuments(
          deckId,
          query,
          connectionId,
        );
        setDroploftDocuments(docs);
      } catch {
        setDroploftDocuments([]);
      } finally {
        setDroploftLoading(false);
      }
    },
    [],
  );

  const handleAddDroploftConnection = useCallback(
    async (input: {
      apiKey: string;
      baseUrl?: string;
      displayName?: string;
    }) => {
      // Throws on bad key (422 from server); panel catches and shows
      // the message so users know to paste a real key.
      await api.addDroploftConnection(input);
      await fetchDroploftConnections();
      if (activeDeck) {
        await fetchDroploftDocuments(activeDeck.id);
      }
    },
    [activeDeck, fetchDroploftConnections, fetchDroploftDocuments],
  );

  const handleDeleteDroploftConnection = useCallback(
    async (connId: string) => {
      try {
        await api.deleteDroploftConnection(connId);
        const remaining = await fetchDroploftConnections();
        if (remaining.length === 0) setDroploftDocuments([]);
      } catch {
        /* leave UI as-is on failure; user can retry */
      }
    },
    [fetchDroploftConnections],
  );

  const handleDroploftSearch = useCallback(
    (q: string) => {
      if (!activeDeck) return;
      const connId = droploftConnections[0]?.id;
      void fetchDroploftDocuments(activeDeck.id, q, connId);
    },
    [activeDeck, droploftConnections, fetchDroploftDocuments],
  );

  const handleDroploftImport = useCallback(
    async (docId: string): Promise<string | null> => {
      if (!activeDeck) return null;
      setDroploftImporting((prev) => new Set(prev).add(docId));
      try {
        const r = await api.importDroploftDocument(
          activeDeck.id,
          docId,
          droploftConnections[0]?.id,
        );
        setDroploftImported((prev) => new Set(prev).add(docId));
        return r.title;
      } catch {
        return null;
      } finally {
        setDroploftImporting((prev) => {
          const next = new Set(prev);
          next.delete(docId);
          return next;
        });
      }
    },
    [activeDeck, droploftConnections],
  );

  const handleDroploftRemove = useCallback(
    async (docId: string) => {
      if (!activeDeck) return;
      try {
        await api.removeDroploftReference(activeDeck.id, docId);
        setDroploftImported((prev) => {
          const next = new Set(prev);
          next.delete(docId);
          return next;
        });
      } catch {
        /* no-op */
      }
    },
    [activeDeck],
  );

  // Refresh Droploft state when the deck or panel opens.
  useEffect(() => {
    if (!droploftOpen || !activeDeck) return;
    (async () => {
      const conns = await fetchDroploftConnections();
      if (conns.length > 0) {
        await fetchDroploftDocuments(activeDeck.id, '', conns[0]!.id);
      }
    })();
  }, [
    droploftOpen,
    activeDeck,
    fetchDroploftConnections,
    fetchDroploftDocuments,
  ]);

  // ─── Confluence handlers ───

  // Once per browser session, when the user enters any deck without
  // an authorized Confluence connection, surface the connect panel
  // as a soft prompt. Skipping is allowed (the panel renders the
  // Connect button rather than blocking the deck); the dismissal is
  // recorded in sessionStorage so subsequent deck navigations within
  // the same session don't re-prompt. Aligns PP main #78 / Connect
  // auto-prompt.
  useEffect(() => {
    if (!activeDeck?.id) return;
    let dismissed = '';
    try {
      dismissed = sessionStorage.getItem('pp_confluence_prompt_dismissed') || '';
    } catch {}
    if (dismissed) return;
    if (Array.isArray(confluenceConnections) && confluenceConnections.length > 0)
      return;
    // Verify live — the in-memory list may be stale on a cold
    // dashboard mount.
    api
      .listConfluenceConnections()
      .then((conns) => {
        if (Array.isArray(conns) && conns.length === 0) {
          try {
            sessionStorage.setItem('pp_confluence_prompt_dismissed', '1');
          } catch {}
          setConfluenceOpen(true);
        } else if (Array.isArray(conns)) {
          setConfluenceConnections(conns);
        }
      })
      .catch(() => {});
  }, [activeDeck?.id, confluenceConnections]);

  const fetchConfluenceConnections = useCallback(async () => {
    try {
      const conns = await api.listConfluenceConnections();
      setConfluenceConnections(conns);
      return conns;
    } catch {
      setConfluenceConnections([]);
      return [];
    }
  }, []);

  const fetchConfluenceSpaces = useCallback(async (deckId: string, connectionId?: string) => {
    try {
      const spaces = await api.listConfluenceSpaces(deckId, connectionId);
      setConfluenceSpaces(spaces);
      setConfluenceSpacesError(null);
      return spaces;
    } catch (e: any) {
      setConfluenceSpaces([]);
      // Pass the error string to the panel so it can render the
      // 'temporarily unavailable' state + Retry button instead of an
      // empty list. Aligns PP main zen panel.
      setConfluenceSpacesError(e?.message || 'Failed to load spaces');
      return [];
    }
  }, []);

  const fetchConfluencePages = useCallback(async (deckId: string, spaceId: string, connectionId?: string) => {
    setConfluenceLoading(true);
    try {
      const pages = await api.listConfluencePages(deckId, spaceId, connectionId);
      setConfluencePages(pages);
    } catch {
      setConfluencePages([]);
    } finally {
      setConfluenceLoading(false);
    }
  }, []);

  // Ref pattern lets the window-event listener call the latest
  // closure of handleConnectConfluence without the listener carrying
  // a stale capture from mount-time. The matching update effect
  // sits right after the handler so the ref points at the live
  // value.
  const handleConnectConfluenceRef = useRef<(() => void) | null>(null);

  // Listen for 'pp:open-confluence-connect' fired by KnowledgePanel
  // in SettingsPage. The connect flow lives in App.tsx (it owns the
  // popup + postMessage wiring); SettingsPage can't call it
  // directly because it has no ref into App. A custom window event
  // keeps the boundary clean.
  useEffect(() => {
    const onOpen = () => {
      handleConnectConfluenceRef.current?.();
    };
    window.addEventListener('pp:open-confluence-connect', onOpen);
    return () => window.removeEventListener('pp:open-confluence-connect', onOpen);
  }, []);

  const handleConnectConfluence = useCallback(() => {
    const url = api.confluenceAuthUrl();
    const popup = window.open(url, 'confluence-oauth', 'width=600,height=700');

    // Listen for postMessage from callback page
    const messageHandler = async (e: MessageEvent) => {
      if (e.data?.type === 'confluence_connected') {
        window.removeEventListener('message', messageHandler);
        clearInterval(pollTimer);
        popup?.close();
        const conns = await fetchConfluenceConnections();
        if (conns.length > 0 && activeDeck) {
          await fetchConfluenceSpaces(activeDeck.id, conns[0].id);
        }
      } else if (e.data?.type === 'confluence_error') {
        window.removeEventListener('message', messageHandler);
        clearInterval(pollTimer);
        popup?.close();
        setMessages(prev => [...prev, {
          id: `sys-${Date.now()}`, role: 'system' as const,
          content: `Confluence connection failed: ${e.data.reason || 'unknown error'}`,
          timestamp: Date.now(),
        }]);
      }
    };
    window.addEventListener('message', messageHandler);

    // Also poll for popup.closed as fallback
    const pollTimer = setInterval(async () => {
      try {
        if (popup?.closed) {
          clearInterval(pollTimer);
          window.removeEventListener('message', messageHandler);
          const conns = await fetchConfluenceConnections();
          if (conns.length > 0 && activeDeck) {
            await fetchConfluenceSpaces(activeDeck.id, conns[0].id);
          }
        }
      } catch {
        clearInterval(pollTimer);
      }
    }, 1000);
    setTimeout(() => { clearInterval(pollTimer); window.removeEventListener('message', messageHandler); }, 300000);
  }, [activeDeck, fetchConfluenceConnections, fetchConfluenceSpaces]);

  // Keep the ref pointing at the latest closure of
  // handleConnectConfluence so the 'pp:open-confluence-connect'
  // listener calls the current handler (not the mount-time one).
  useEffect(() => {
    handleConnectConfluenceRef.current = handleConnectConfluence;
  }, [handleConnectConfluence]);

  const handleSelectSpace = useCallback(async (spaceId: string) => {
    setSelectedSpaceId(spaceId);
    // Reset tree state on space change
    setConfluenceExpandedPages(new Set());
    setConfluenceChildPages(new Map());
    setConfluenceLoadingChildren(new Set());
    if (!spaceId || !activeDeck) {
      setConfluencePages([]);
      return;
    }
    const connectionId = confluenceConnections[0]?.id;
    await fetchConfluencePages(activeDeck.id, spaceId, connectionId);
  }, [activeDeck, confluenceConnections, fetchConfluencePages]);

  const handleReferencePage = useCallback(async (pageId: string) => {
    if (!activeDeck) return;
    setReferencingPageIds(prev => new Set(prev).add(pageId));
    try {
      const connectionId = confluenceConnections[0]?.id;
      const allPages = [...confluencePages, ...[...confluenceChildPages.values()].flat()];
      const pageType = allPages.find(p => p.id === pageId)?.type;
      await api.referenceConfluencePage(activeDeck.id, pageId, connectionId, pageType);
      const ctx = await api.listContext(activeDeck.id);
      setContextItems(ctx);
      // Optimistically update referenced status
      setConfluencePages(prev => prev.map(p => p.id === pageId ? { ...p, referenced: true } : p));
      setConfluenceChildPages(prev => {
        const next = new Map(prev);
        for (const [parentId, children] of next) {
          const idx = children.findIndex((c: any) => c.id === pageId);
          if (idx !== -1) {
            const updated = [...children];
            updated[idx] = { ...updated[idx], referenced: true };
            next.set(parentId, updated);
            break;
          }
        }
        return next;
      });
      // Return title for batch message
      const page = allPages.find(p => p.id === pageId);
      return page?.title || pageId;
    } catch {
      return null;
    } finally {
      setReferencingPageIds(prev => {
        const next = new Set(prev);
        next.delete(pageId);
        return next;
      });
    }
  }, [activeDeck, confluenceConnections, confluencePages, confluenceChildPages]);

  const handleRemoveReference = useCallback(async (pageId: string) => {
    if (!activeDeck) return;
    try {
      await api.removeConfluenceReference(activeDeck.id, pageId);
      // Refresh context items
      const ctx = await api.listContext(activeDeck.id);
      setContextItems(ctx);

      // Compute removed page title and remaining list before optimistic update
      const allPages = [...confluencePages, ...[...confluenceChildPages.values()].flat()];
      const removedPage = allPages.find(p => p.id === pageId);
      const removedTitle = removedPage?.title || pageId;
      const remaining = allPages.filter(p => p.referenced && p.id !== pageId);
      const remainingList = remaining.length > 0
        ? remaining.map(p => p.title).join(', ')
        : '(none)';

      // Optimistically update referenced status in confluencePages
      setConfluencePages(prev => prev.map(p => p.id === pageId ? { ...p, referenced: false } : p));
      // Optimistically update referenced status in confluenceChildPages
      setConfluenceChildPages(prev => {
        const next = new Map(prev);
        for (const [parentId, children] of next) {
          const idx = children.findIndex((c: any) => c.id === pageId);
          if (idx !== -1) {
            const updated = [...children];
            updated[idx] = { ...updated[idx], referenced: false };
            next.set(parentId, updated);
            break;
          }
        }
        return next;
      });

      // Notify agents about the removal (silent — no chat bubble)
      const agentMsg = `I removed the Confluence reference "${removedTitle}" from memory. Remaining files: ${remainingList}.`;
      sendMessageRef.current?.(agentMsg, { skipLocalMessage: true });
    } catch {}
  }, [activeDeck, confluencePages, confluenceChildPages]);

  const handleToggleExpandPage = useCallback(async (pageId: string) => {
    setConfluenceExpandedPages(prev => {
      const next = new Set(prev);
      if (next.has(pageId)) next.delete(pageId);
      else next.add(pageId);
      return next;
    });

    // If expanding and children not loaded yet, fetch via CQL
    if (!confluenceExpandedPages.has(pageId) && !confluenceChildPages.has(pageId)) {
      if (!activeDeck) return;
      setConfluenceLoadingChildren(prev => new Set(prev).add(pageId));
      try {
        const connectionId = confluenceConnections[0]?.id;
        const spaceKey = selectedSpaceId;
        const children = await api.listConfluencePages(activeDeck.id, spaceKey || '', connectionId, pageId);
        setConfluenceChildPages(prev => new Map(prev).set(pageId, children));
        // If no children found, update the page to show as leaf (file icon)
        if (children.length === 0) {
          setConfluencePages(prev => prev.map(p => p.id === pageId ? { ...p, hasChildren: false } : p));
          // Also update in child pages maps
          setConfluenceChildPages(prev => {
            const next = new Map(prev);
            for (const [k, v] of next) {
              next.set(k, v.map(p => p.id === pageId ? { ...p, hasChildren: false } : p));
            }
            return next;
          });
        }
      } catch {
        setConfluenceChildPages(prev => new Map(prev).set(pageId, []));
      } finally {
        setConfluenceLoadingChildren(prev => {
          const next = new Set(prev);
          next.delete(pageId);
          return next;
        });
      }
    }
  }, [activeDeck, selectedSpaceId, confluenceConnections, confluenceExpandedPages, confluenceChildPages]);

  const handlePreviewPage = useCallback((pageId: string) => {
    // Find the page's Confluence URL and open it directly
    const allPages = [...confluencePages, ...[...confluenceChildPages.values()].flat()];
    const page = allPages.find(p => p.id === pageId);
    if (page?.url) {
      // url is relative like /spaces/DT/pages/123/Title — prepend cloud URL
      const cloudUrl = confluenceConnections[0]?.cloud_url || '';
      const fullUrl = page.url.startsWith('http') ? page.url : `${cloudUrl}/wiki${page.url}`;
      window.open(fullUrl, '_blank');
    }
  }, [confluencePages, confluenceChildPages, confluenceConnections]);

  // Fetch Confluence connections on deck change
  useEffect(() => {
    if (!activeDeck) {
      setConfluenceConnections([]);
      setConfluenceSpaces([]);
      setConfluencePages([]);
      setSelectedSpaceId('');
      setConfluenceExpandedPages(new Set());
      setConfluenceChildPages(new Map());
      setConfluenceLoadingChildren(new Set());
        return;
    }
    (async () => {
      const conns = await fetchConfluenceConnections();
      if (conns.length > 0) {
        await fetchConfluenceSpaces(activeDeck.id, conns[0].id);
      }
    })();
  }, [activeDeck?.id, fetchConfluenceConnections, fetchConfluenceSpaces]);

  const sendMessage = useCallback((content: string, opts?: { displayContent?: string; messageType?: 'file' | 'folder'; skipLocalMessage?: boolean; attachments?: string[]; imageAttachments?: import('./api/client').ChatAttachment[] }) => {
    if (!activeDeck) return;
    if (activeDeck.access && activeDeck.access.capabilities.chat === false) {
      const now = Date.now();
      setMessages(prev => [...prev, {
        id: `readonly-${now}`,
        role: 'system',
        content: 'You have read-only access to this workspace.',
        timestamp: now,
      }]);
      return;
    }

    // Optimistic insert — render immediately so the user sees feedback even if
    // the SSE user_message broadcast is delayed or arrives after the event stream
    // reconnected. Use a temp id prefixed "local-" so the onUserMessage dedup
    // (which matches on real message_id) doesn't accidentally keep the temp one.
    // When the SSE broadcast arrives with the real message_id, we swap it in.
    //
    // For attachment+text sends, mirror the expandRefMessage shape so
    // the live optimistic view matches what reload renders: N file
    // cards (anchor + siblings) + 1 text bubble. The first file card
    // carries the dedup anchor (pendingLocalMessageRef.localId);
    // siblings use `<localId>-ref-N` / `<localId>-text` so onUserMessage's
    // re-key visit can map them to `<messageId>-ref-N`/-text matching
    // the REST-rehydrate ids from expandRefMessage (aligns PP main #114).
    const optimisticUserTimelineIds: string[] = [];
    if (!opts?.skipLocalMessage) {
      const localId = `local-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
      pendingLocalMessageRef.current = { localId, content };
      const hasAttachments = opts?.messageType === 'file' && opts?.attachments && opts.attachments.length > 0;
      if (hasAttachments) {
        const baseTs = Date.now();
        const fileNames = opts!.attachments!;
        const fileCards: ChatMessage[] = fileNames.map((name, idx) => ({
          id: idx === 0 ? localId : `${localId}-ref-${idx}`,
          role: 'user' as const,
          content: `[REF:file] ${name}`,
          displayContent: name,
          messageType: 'file' as const,
          fileKey: name,
          timestamp: baseTs + idx,
        }));
        const optimistic: ChatMessage[] = [...fileCards];
        const textTrim = (content || '').trim();
        if (textTrim) {
          optimistic.push({
            id: `${localId}-text`,
            role: 'user' as const,
            content: textTrim,
            timestamp: baseTs + fileNames.length,
          });
        }
        setMessages(prev => [...prev, ...optimistic]);
        optimisticUserTimelineIds.push(...optimistic.map(msg => `user:${msg.id}`));
        setAgentTimelineItems(prev => sortAgentTimelineItems([...prev, ...optimistic.map((msg): AgentTimelineItem => ({
          id: `user:${msg.id}`,
          kind: 'chat.user',
          turnId: '',
          ts: msg.timestamp,
          content: msg.displayContent || msg.content,
          attachments: msg.attachments,
          status: 'done',
        }))]));
      } else {
        const ts = Date.now();
        const optimisticMessage: ChatMessage = {
          id: localId,
          role: 'user' as const,
          content: opts?.displayContent || content,
          timestamp: ts,
          messageType: opts?.messageType,
          attachments: opts?.imageAttachments,
        };
        setMessages(prev => [...prev, optimisticMessage]);
        optimisticUserTimelineIds.push(`user:${localId}`);
        const optimisticTimelineItem: AgentTimelineItem = {
          id: `user:${localId}`,
          kind: 'chat.user',
          turnId: '',
          ts,
          content: optimisticMessage.content,
          attachments: optimisticMessage.attachments,
          status: 'done',
        };
        setAgentTimelineItems(prev => sortAgentTimelineItems([...prev, optimisticTimelineItem]));
      }
    }

    // Auto-rename deck on first real user message
    if (!activeDeck.title_manual && content !== 'start' && content !== 'build' && content !== 'plan' && !content.startsWith('[Resume]') && !content.startsWith('I updated ') && !content.startsWith('I edited ') && !content.startsWith('I removed ') && messages.filter(m => m.role === 'user').length === 0) {
      const title = content.slice(0, 50) + (content.length > 50 ? '...' : '');
      api.updateDeck(activeDeck.id, { title } as any).then((updated) => {
        if (updated) setActiveDeck(updated);
        refreshDecks();
      }).catch(() => {});
    }

    // ── Stream Architecture: POST-only, response comes through SSE hooks ──
    {
      const alreadyGenerating = isGenerating;
      setIsGenerating(true);
      if (!alreadyGenerating) clearActivity();
      streamTextsRef.current = {};
      activeDeckIdForTimer.current = activeDeck.id;
      sessionStorage.setItem(`pp_activity_start_${activeDeck.id}`, String(Date.now()));
      const localActivityTurnId = seedLocalActivityTurn({ append: alreadyGenerating });
      const sentDeckId = activeDeck.id;

      const sendAfterOptionalInterrupt = async () => {
        if (isGenerating) {
          void interruptDeckTurn(activeDeck.id, 'user_barge_in').catch((err) => {
            console.warn('[interrupt] Soft interrupt request failed:', err);
          });
        }
        return sendMessageOnly(activeDeck.id, content, opts?.attachments, {
          imageAttachments: opts?.imageAttachments,
        });
      };

      sendAfterOptionalInterrupt().then(({ turn_id, queued }) => {
        if (activeDeckIdRef.current !== sentDeckId) return;
        bindOptimisticUserTimelineTurn(optimisticUserTimelineIds, turn_id);
        if (pendingLocalActivityTurnIdRef.current === localActivityTurnId) {
          bindLocalActivityTurn(turn_id);
        }
        if (queued) {
          setActivityItems(prev => prev.map(item =>
            item.turnId === turn_id && item.tool === 'queued'
              ? { ...item, detail: 'Queued' }
              : item
          ));
          setTurns(prev => prev.map(turn =>
            turn.turnId === turn_id
              ? {
                  ...turn,
                  items: turn.items.map(item =>
                    item.tool === 'queued' ? { ...item, detail: 'Queued' } : item
                  ),
                }
              : turn
          ));
          updateLocalQueuedTimeline(turn_id, {
            title: 'Queued',
            detail: 'Waiting for the current agent turn to finish.',
            status: 'pending',
          });
          return;
        }
        activeActivityTurnIdRef.current = turn_id;
        if (pendingLocalActivityTurnIdRef.current === turn_id) {
          pendingLocalActivityTurnIdRef.current = null;
        }
        // Hot bridge: the persistent agent is already primed (warmup done),
        // so there's no real container start / workspace prep this turn —
        // labelling it "Starting agent" reads as a cold start on every send.
        // Show a neutral "Thinking" placeholder instead and let the real
        // activity stream replace it. Only a genuinely cold deck (warmup not
        // yet finished) shows the start/prepare copy.
        const warm = agentWarmRef.current;
        const stepDetail = warm ? 'Thinking' : 'Starting agent';
        const timelineDetail = warm
          ? 'The agent is working on your request.'
          : 'Preparing the agent workspace.';
        setActivityItems(prev => prev.map(item =>
          item.turnId === turn_id && item.tool === 'queued'
            ? { ...item, detail: stepDetail, status: 'running' }
            : item
        ));
        setTurns(prev => prev.map(turn =>
          turn.turnId === turn_id
            ? {
                ...turn,
                status: 'live',
                items: turn.items.map(item =>
                  item.tool === 'queued'
                    ? { ...item, detail: stepDetail, status: 'running' }
                    : item
                ),
              }
            : turn
        ));
        updateLocalQueuedTimeline(turn_id, {
          title: stepDetail,
          detail: timelineDetail,
          status: 'running',
        });
      }).catch((err) => {
        console.error('[send] Failed:', err.message);
        setMessages(prev => [...prev, {
          id: `err-${Date.now()}`, role: 'system',
          content: err.message || 'Failed to send message',
          timestamp: Date.now(),
        }]);
        setIsGenerating(false);
        if (pendingLocalActivityTurnIdRef.current === localActivityTurnId) {
          pendingLocalActivityTurnIdRef.current = null;
          activeActivityTurnIdRef.current = null;
        }
        updateLocalQueuedTimeline(localActivityTurnId, {
          title: 'Send failed',
          detail: err.message || 'Failed to send message',
          status: 'failed',
        });
        clearActivity();
      });
      return;
    }

  }, [activeDeck, bindLocalActivityTurn, bindOptimisticUserTimelineTurn, isGenerating, messages, refreshDecks, seedLocalActivityTurn, updateLocalQueuedTimeline]);
  // Keep ref in sync so handleReferencePage can call sendMessage without circular deps
  useEffect(() => { sendMessageRef.current = sendMessage; });

  // Listen for edit-saved events from PreviewPanel (custom event avoids stale closure)
  useEffect(() => {
    function handleEditSaved(e: Event) {
      const detail = (e as CustomEvent).detail ?? {};
      const what = detail.what;
      const slideNum = typeof detail.slideNum === 'number' ? detail.slideNum : null;
      // Anchor the iframe at the slide the user just edited so the
      // post-save view doesn't jump to slide 1. The iframe `key`
      // includes editMode, so flipping out of edit always remounts;
      // hash navigation is what restores scroll position.
      if (slideNum && slideNum > 0) {
        setActiveSlideHash(`#slide-${String(slideNum).padStart(2, '0')}`);
      }
      if (what) sendMessageRef.current?.(what, { skipLocalMessage: true });
    }
    window.addEventListener('pp:edit-saved', handleEditSaved);
    return () => window.removeEventListener('pp:edit-saved', handleEditSaved);
  }, []);

  // Auto-greeting disabled — replaced by quick start suggestion buttons in ChatPanel.
  // User clicks a suggestion to send the first message, which is more intentional.
  const [messagesLoaded, setMessagesLoaded] = useState<{ deckId: string; count: number } | null>(null);
  // Set to the current deck id once selectDeck / initial-load has finished
  // hydrating contextItems (Promise.all done + setContextItems called).
  // Used by ConceptCloud to distinguish "parent is still loading context"
  // (prefetchedContext === [], wait) from "parent has loaded; context is
  // genuinely empty" (prefetchedContext === [], proceed to derive-from-
  // props). Without this signal ConceptCloud sees an empty array on
  // mount and falls through to its own listContext fetch — a 2-3s
  // duplicate on every deck-open hot path.
  const [contextHydratedDeckId, setContextHydratedDeckId] = useState<string | null>(null);

  const clearMessages = useCallback(async () => {
    if (!activeDeck) return;
    try {
      await api.clearMessages(activeDeck.id);
      setMessages([]);
      setAgentTimelineItems([]);
      setAgentStatuses({});
    } catch (err) {
      console.error('Failed to clear messages:', err);
    }
  }, [activeDeck]);

  const stopGeneration = useCallback(() => {
    if (activeDeck) {
      void interruptDeckTurn(activeDeck.id, 'user_interrupt').catch((err) => {
        console.warn('[interrupt] Soft interrupt request failed:', err);
      });
      const interruptItem: AgentTimelineItem = {
        id: `local-interrupt-${Date.now()}`,
        kind: 'agent.tool',
        category: 'system',
        title: 'Interrupt requested',
        detail: 'Waiting for the agent to stop the current turn.',
        status: 'done',
        turnId: activeActivityTurnIdRef.current || '',
        ts: Date.now(),
        raw: { localInterrupt: true },
      };
      setAgentTimelineItems(prev => sortAgentTimelineItems([...prev, interruptItem]));
      return;
    }
    setIsGenerating(false);
    clearActivity();
    setMessages(prev => prev.map(m =>
      m.id === 'streaming' ? { ...m, id: `msg-${Date.now()}` } : m
    ));
  }, [activeDeck]);

  const [isExporting, setIsExporting] = useState(false);
  const handleExport = useCallback(async () => {
    if (!activeDeck || isExporting) return;
    setIsExporting(true);
    try {
      const res = await apiFetch(`/api/decks/${activeDeck.id}/export`);
      if (res.ok) {
        const blob = await res.blob();
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `${activeDeck.title || 'presentation'}.html`;
        a.click();
        URL.revokeObjectURL(url);
      } else {
        const err = await res.json().catch(() => ({ error: res.statusText }));
        notifyError(err.error ? `Export failed: ${err.error}` : `Export failed (${res.status})`);
      }
    } catch (err) {
      if (err instanceof AuthExpiredError) return; // Modal already shown
      notifyError(`Export failed: ${(err as Error)?.message || 'network error'}`);
    }
    setIsExporting(false);
  }, [activeDeck, isExporting]);

  // Resizable chat divider
  const handleDividerMouseDown = useCallback((e: React.MouseEvent) => {
    e.preventDefault();
    setIsDragging(true);
    const startX = e.clientX;
    const startWidth = chatWidth;
    const onMove = (ev: MouseEvent) => {
      const delta = ev.clientX - startX;
      setChatWidth(Math.max(280, Math.min(window.innerWidth - 280, startWidth + delta)));
      setUserDragged(true);
    };
    const onUp = () => {
      document.removeEventListener('mousemove', onMove);
      document.removeEventListener('mouseup', onUp);
      document.body.style.cursor = '';
      document.body.style.userSelect = '';
      setIsDragging(false);
    };
    document.addEventListener('mousemove', onMove);
    document.addEventListener('mouseup', onUp);
    document.body.style.cursor = 'col-resize';
    document.body.style.userSelect = 'none';
  }, [chatWidth]);

  // Merge demo data into contextItems so both Sidebar and Manage page show them
  const effectiveContextItems = React.useMemo(() => {
    // In production, only use real backend data — no demo fallbacks
    if (!USE_DEMO_DATA) return contextItems;
    const items = [...contextItems];
    const keys = new Set(items.map(c => c.key));
    DEMO_MEMORIES.forEach(m => {
      const k = `memory/${m.name}`;
      if (!keys.has(k)) items.push({ key: k, content: m.content });
    });
    DEMO_CONTEXT_FILES.forEach(f => {
      const k = `context/${f.name}`;
      if (!keys.has(k)) items.push({ key: k, content: f.content });
    });
    DEMO_SKILLS.forEach(s => {
      s.files.forEach(f => {
        const k = `skill/${s.name}/${f.name}`;
        if (!keys.has(k)) items.push({ key: k, content: f.content });
      });
    });
    return items;
  }, [contextItems]);

  // Derive disabled keys from context items' enabled field (from Arx Memory)
  const disabledKeys = React.useMemo(() => {
    const keys = new Set<string>();
    effectiveContextItems.forEach(c => {
      if (c.enabled === 0) keys.add(c.key);
    });
    return keys;
  }, [effectiveContextItems]);

  // Derive referenced Confluence pages for chat pills
  const referencedPages = React.useMemo(() => {
    const allPages = [...confluencePages, ...[...confluenceChildPages.values()].flat()];
    return allPages.filter(p => p.referenced).map(p => ({ id: p.id, title: p.title }));
  }, [confluencePages, confluenceChildPages]);

  // Build file list from effectiveContextItems (all data from backend + demo fallback)
  // Key convention: "memory/file.md", "context/file.md", "skill/GroupName/file.md"
  const buildEditFiles = useCallback(() => {
    const allFiles: { id: string; name: string; content: string; section: 'memory' | 'context' | 'skill'; skillGroup?: string }[] = [];

    effectiveContextItems.forEach((c, i) => {
      if (!c.content) return;
      if (c.key.startsWith('memory/')) {
        allFiles.push({ id: `mem-${i}`, name: c.key.slice(7), content: c.content, section: 'memory' });
      } else if (c.key.startsWith('skill/')) {
        const rest = c.key.slice(6);
        const slashIdx = rest.indexOf('/');
        if (slashIdx > 0) {
          allFiles.push({
            id: `skill-${i}`, name: rest.slice(slashIdx + 1),
            content: c.content, section: 'skill', skillGroup: rest.slice(0, slashIdx),
          });
        }
      } else {
        const name = c.key.startsWith('context/') ? c.key.slice(8) : c.key;
        allFiles.push({ id: `ctx-${i}`, name, content: c.content, section: 'context' });
      }
    });

    return allFiles;
  }, [effectiveContextItems]);

  // Edit mode — enter directly (no confirmation modal)
  const handleManage = useCallback((_type: EditType) => {
    const files = buildEditFiles();
    setEditMode({ files, activeFile: files[0]?.name || null });
  }, [buildEditFiles]);

  // Edit mode save handler
  const handleEditSave = useCallback(async () => {
    if (!editMode) return;
    if (activeDeck) {
      for (const f of editMode.files) {
        let key: string;
        if (f.section === 'memory') key = `memory/${f.name}`;
        else if (f.section === 'skill') key = `skill/${f.skillGroup || 'Custom'}/${f.name}`;
        else key = `context/${f.name}`;
        await api.saveContext(activeDeck.id, key, f.content).catch(() => {});
      }
      const ctx = await api.listContext(activeDeck.id).catch(() => []);
      setContextItems(ctx);
    }
    setEditMode(null);
  }, [editMode, activeDeck]);

  const handleBack = useCallback(() => {
    // Invalidate stale SSE callbacks BEFORE aborting
    activeDeckIdRef.current = null;
    setIsGenerating(false);
    clearActivity();
    setActiveDeck(null);
    navigate(activeWorkspaceId ? `/w/${activeWorkspaceId}` : '/');
    setMessages([]);
    setAgentTimelineItems([]);
    setPlanMarkdown('');
    setContextItems([]);
    setAgentStatuses({});
    setDeckMembers([]);
    setPreviewRefreshKey(0);
    refreshDecks();
  }, [refreshDecks]);

  const deleteDeck = useCallback(async (id: string) => {
    try {
      await api.deleteDeck(id);
      await refreshDecks();
    } catch {}
  }, [refreshDecks]);

  const renameDeck = useCallback(async (id: string, title: string) => {
    try {
      await api.updateDeck(id, { title } as any);
      await refreshDecks();
    } catch {}
  }, [refreshDecks]);

  // Derive unified UI state for observability
  const phase = activeDeck ? computePanelPhase(activeDeck, contextItems, artifacts) : null;

  // Sync chat width from phase + panelOpen (unless user manually dragged)
  const deckUIState = (() => {
    if (!activeDeck) return 'DASHBOARD';
    if (isWarmingUp) return 'WARMUP';
    if (planTask === 'create') return 'PLAN_CREATING';
    if (planTask === 'update') return 'PLAN_UPDATING';
    if (previewTask === 'create') return 'PREVIEW_CREATING';
    if (previewTask === 'update') return 'PREVIEW_UPDATING';
    if (phase === 'preview') return 'PREVIEW_READY';
    if (phase === 'plan-ready') return 'PLAN_READY';
    if (!agentOnline) return 'AGENT_OFFLINE';
    return 'CHATTING';
  })();

  // Panel opens when displayable content exists
  const hasBriefContent = contextItems.some(c =>
    (c.key === 'memory/audience.json' || c.key === 'memory/goal.json') && c.content
  );
  useEffect(() => {
    if (!panelOpen) {
      const hasContent = hasBriefContent || !!planMarkdown || !!notesMarkdown
        || (artifacts.length > 0 && artifacts.some(a => a.filename?.endsWith('.html')))
        // Built deck artifacts reported by the deck's signals
        // (artifact-grouper). A deck that skipped gather/plan and went
        // straight to a build has only these — no brief/plan/notes
        // markdown — so without this it opened to chat-only.
        || ((activeDeck?.signals?.artifacts?.length ?? 0) > 0)
        // CF-rebuild signal: previewRefreshKey gets bumped when the
        // agent emits a presentation_created activity row, even when
        // no R2 artifacts exist.
        || (previewRefreshKey ?? 0) > 0;
      if (hasContent) setPanelOpen(true);
    }
  }, [hasBriefContent, planMarkdown, notesMarkdown, artifacts, panelOpen, previewRefreshKey, activeDeck?.signals?.artifacts]);

  // Adjust chat width — right panel always takes major ratio (61.8%)
  useEffect(() => {
    if (userDragged) return;
    if (!panelOpen) {
      setChatWidth(Math.min(Math.round(window.innerWidth * 0.4), 520));
    } else {
      // Chat = 38.2% (minor), Panel = 61.8% (major) — always
      setChatWidth(Math.round(window.innerWidth * (1 - 1 / PHI)));
    }
  }, [panelOpen, phase, previewTask, userDragged, PHI]);

  useEffect(() => {
    (window as any).__PP_STATE = {
      deckUIState,
      phase,
      planTask,
      notesTask,
      previewTask,
      isGenerating,
      isWarmingUp,
      agentOnline,
      agentState,
      currentActivity,
      activeDeckId: activeDeck?.id ?? null,
      maxPhaseReached,
    };
  });

  // Loading states
  if (authLoading) {
    return (
      <div style={{ height: 'var(--app-height)', display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'var(--bg-primary)' }}>
        <div style={{ width: 20, height: 20, borderRadius: '50%', border: '1.5px solid #eee', borderTopColor: '#bbb', animation: 'pp-spin 1s linear infinite' }} />
        <style>{`@keyframes pp-spin { to { transform: rotate(360deg); } }`}</style>
      </div>
    );
  }

  // Not logged in — show login, remember where to go after
  if (!user) {
    // Save intended destination for post-login redirect (skip /login itself)
    const redirectAfterLogin = route.page !== 'login' && route.page !== 'workspace'
      ? window.location.pathname
      : null;
    return <LoginPage onLogin={(data) => {
      handleLogin(data);
      if (redirectAfterLogin) {
        navigate(redirectAfterLogin, { replace: true });
      }
    }} />;
  }

  // Already logged in but on /login route — redirect to workspace
  if (route.page === 'login') {
    navigate('/', { replace: true });
  }

  // App loading
  if (appLoading || openingDeckId) {
    return (
      <div style={{ height: 'var(--app-height)', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', background: 'var(--bg-primary)', gap: 16 }}>
        <style>{`@keyframes pp-spin { to { transform: rotate(360deg); } }`}</style>
        <div style={{
          width: 20, height: 20, borderRadius: '50%',
          border: '1.5px solid #eee',
          borderTopColor: '#bbb',
          animation: 'pp-spin 1s linear infinite',
        }} />
        <p style={{ fontSize: 'var(--fs-sm)', color: '#ccc', fontWeight: 300 }}>
          {openingDeckId ? 'Opening deck...' : 'Setting things up...'}
        </p>
      </div>
    );
  }

  // Admin dashboard
  if (route.page === 'admin') {
    // Hard-gate the admin shell so a non-admin user typing /admin in
    // the URL bar doesn't see the dashboard chrome (the endpoints
    // it calls 403 anyway, but the chrome was rendering an error-
    // riddled empty page). Redirect home until /api/admin/me confirms
    // they're admin.
    if (!isAdminFromServer) {
      navigate('/');
      return null;
    }
    return (
      <AdminDashboard onBack={() => navigate('/')} />
    );
  }

  // Settings page
  if (route.page === 'settings') {
    const myProfile = profiles[user?.id || ''] || null;
    const activeWorkspace =
      workspaces.find((workspace) => workspace.id === activeWorkspaceId) ?? null;
    return (
      <SettingsPage
        username={user?.username || ''}
        profile={myProfile}
        workspaceId={activeWorkspaceId}
        workspaceName={activeWorkspace?.name ?? null}
        onBack={() => navigate('/')}
        onProfileUpdate={(updated) => {
          setProfiles(prev => ({ ...prev, [updated.id]: updated }));
        }}
      />
    );
  }

  // No deck selected — dashboard. Render OnboardingTour as a sibling
  // so first-login users see the welcome flow the moment they land on
  // the dashboard, not only after they've drilled into a deck.
  if (!activeDeck) {
    return (
      <>
        <DeckDashboard
          decks={decks}
          onSelectDeck={selectDeck}
          onCreateDeck={createDeck}
          onDeleteDeck={deleteDeck}
          onRenameDeck={renameDeck}
          isCreating={isCreating}
          theme={theme}
          onToggleTheme={toggleTheme}
          onLogout={() => { logout(); navigate('/'); }}
          onOpenSettings={() => navigate('/settings')}
          onOpenAdmin={() => navigate('/admin')}
          isAdmin={isAdminFromServer || !!profiles[user?.id || '']?.is_admin}
          username={user?.username}
          userRole={user?.role || 'user'}
          workspaces={workspaces}
          activeWorkspace={activeWorkspaceId ?? undefined}
          onSelectWorkspace={handleSelectWorkspace}
          onRefreshDecks={refreshDecks}
          onCreateWorkspace={async (name: string) => {
            try {
              const created = await api.createWorkspace(name);
              const ws = await api.listWorkspaces();
              setWorkspaces(ws);
              await handleSelectWorkspace(created.id);
              navigate(`/w/${created.id}`);
            } catch {}
          }}
        />
        <OnboardingTour enabled={!!user} />
      </>
    );
  }

  // Workspace is ready once warmup completes (container running).
  // Agent may still be loading (agentState='online'), but user can see the workspace.
  // Chat panel shows "Agent loading..." indicator until agentOnline=true.
  const workspaceReady = !isWarmingUp;

  // When preview:create is active but no HTML artifact exists yet,
  // show an overlay on the Plan tab so the user knows preview is being generated.
  const previewGeneratingNoHTML = previewTask === 'create' && !artifacts.some(a => a.filename?.toLowerCase().endsWith('.html'));

  // Main 3-column layout
  return (
    <div style={{ height: 'var(--app-height)', display: 'flex', flexDirection: 'column', position: 'relative' }}>
      <ConnectionBanner status={activeDeck ? connectionStatus : 'connected'} onManualReconnect={handleManualReconnect} />
      {activeDeck && newMessagesCount > 0 && (
        <NewMessagesBanner count={newMessagesCount} onClick={handleScrollToNewMessages} />
      )}
      {!workspaceReady && (
        <div style={{
          position: 'absolute', inset: 0, zIndex: 50,
          background: 'var(--bg-primary)',
          display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
          gap: 16,
        }}>
          {warmupPhase !== 'error' ? (
            <>
              <div style={{
                width: 20, height: 20, borderRadius: '50%',
                border: '1.5px solid #eee',
                borderTopColor: '#bbb',
                animation: 'spin 1s linear infinite',
              }} />
              <p style={{ fontSize: 'var(--fs-sm)', color: '#ccc', fontWeight: 300 }}>
                {warmupPhase === 'booting' ? 'Starting agent container...' : 'Connecting to agent...'}
              </p>
            </>
          ) : (
            <>
              <div style={{ fontSize: 20, color: '#e55' }}>!</div>
              <p style={{ fontSize: 'var(--fs-sm)', color: '#e55', fontWeight: 400, textAlign: 'center', maxWidth: 360, lineHeight: 1.5 }}>
                {warmupError}
              </p>
              <div style={{ display: 'flex', gap: 12, marginTop: 4 }}>
                <button onClick={handleRetryWarmup} style={{
                  padding: '6px 20px',
                  background: 'var(--brand, #0033FF)', border: 'none',
                  borderRadius: 4, cursor: 'pointer',
                  color: '#fff', fontSize: 'var(--fs-sm)',
                  fontFamily: 'inherit',
                }}>
                  Retry
                </button>
                <button onClick={handleCancelWarmup} style={{
                  padding: '6px 20px',
                  background: 'none', border: '1px solid #eee',
                  borderRadius: 4, cursor: 'pointer',
                  color: '#ccc', fontSize: 'var(--fs-sm)',
                  fontFamily: 'inherit',
                }}>
                  Cancel
                </button>
              </div>
            </>
          )}
          {warmupPhase !== 'error' && (
            <button onClick={handleCancelWarmup} style={{
              marginTop: 8, padding: '6px 20px',
              background: 'none', border: '1px solid #eee',
              borderRadius: 4, cursor: 'pointer',
              color: '#ccc', fontSize: 'var(--fs-sm)',
              fontFamily: 'inherit', transition: 'all 0.3s',
            }}>
              Cancel
            </button>
          )}
        </div>
      )}
      {/* Floating nav — frosted glass bar */}
      <div className="pp-nav-float" style={{
        position: 'fixed', top: 0, left: 0, right: 0, zIndex: 50,
        height: 48, display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        padding: '0 24px',
        background: 'var(--bg-glass)',
        backdropFilter: 'blur(20px) saturate(180%)',
        WebkitBackdropFilter: 'blur(20px) saturate(180%)',
      }}>
        <span
          onClick={handleBack}
          style={{ fontSize: 'var(--fs-xs)', color: 'var(--text-secondary)', fontWeight: 400, letterSpacing: '0.02em', cursor: 'pointer', transition: 'color 0.2s' }}
          onMouseOver={e => (e.currentTarget.style.color = 'var(--text-primary)')}
          onMouseOut={e => (e.currentTarget.style.color = 'var(--text-secondary)')}
        >
          ← PitchPilot
        </span>
        {/* Nav-Meta: deck title + active-time chip + agent badge.
            DeckTimeChip live-ticks MM:SS while a turn is in flight;
            clicking it opens the stats popover. Aligns PP main
            DeckTimeChip + DeckStatsPopover. */}
        <div className={`nav-meta${statsOpen ? ' pp-nav-meta-show' : ''}`} style={{
          display: 'flex', alignItems: 'center', gap: 10,
          opacity: 0, transition: 'opacity 0.3s',
        }}>
          {activeDeck && (
            <DeckTimeChip
              ref={statsChipRef}
              totalActiveMs={activeDeck.signals?.total_active_ms ?? 0}
              taskStartedAt={activeDeck.signals?.task_started_at ?? null}
              onClick={() => setStatsOpen(o => !o)}
            />
          )}
          <span style={{ fontSize: 'var(--fs-xs)', color: 'var(--text-secondary)', fontWeight: 400 }}>
            {activeDeck?.title || 'Untitled'}
          </span>
          <span style={{
            fontSize: 'var(--fs-2xs)', fontWeight: 500, letterSpacing: '0.05em',
            padding: '1px 6px', borderRadius: 3,
            background: 'rgba(0,0,0,0.04)', color: 'var(--text-secondary)',
            textTransform: 'uppercase',
          }}>
            {currentTulpaLabel || 'Writer'}
          </span>
        </div>
      </div>
      <style>{`
        .pp-nav-float:hover .nav-meta { opacity: 1 !important; }
        .pp-nav-float .nav-meta.pp-nav-meta-show { opacity: 1 !important; }
      `}</style>
      {statsOpen && activeDeck && (
        <DeckStatsPopover
          anchorRect={statsChipRef.current?.getBoundingClientRect() ?? null}
          onClose={() => setStatsOpen(false)}
          deckTitle={activeDeck.title}
          totalActiveMs={activeDeck.signals?.total_active_ms ?? 0}
          taskStartedAt={activeDeck.signals?.task_started_at ?? null}
          versions={(() => {
            // Derive a unique version-label list from signals.artifacts[].
            // The popover only needs labels — artifacts[] are not used.
            const seen = new Set<string>();
            const out: { label: string }[] = [];
            for (const a of activeDeck.signals?.artifacts ?? []) {
              if (a.version_label && !seen.has(a.version_label)) {
                seen.add(a.version_label);
                out.push({ label: a.version_label });
              }
            }
            return out;
          })()}
          versionsActiveMs={activeDeck.signals?.versions_active_ms ?? {}}
          taskVersion={activeDeck.signals?.task_version ?? null}
        />
      )}

      <div className="pp-workspace-layout" style={{ flex: 1, display: 'flex', minHeight: 0, paddingTop: 48 }}>
        {editMode ? (
          /* Edit mode: editor pane fills the rest */
          <EditModePage
            files={editMode.files}
            activeFileName={editMode.activeFile}
            onFileChange={(newFiles) => setEditMode(prev => prev ? { ...prev, files: newFiles } : prev)}
          />
        ) : (
          <>
            {/* Center: Chat */}
            {chatCollapsed ? (
              <ChatPanel
                messages={messages}
                onSend={sendMessage}
                onStop={stopGeneration}
                onClearMessages={clearMessages}
                onUploadFile={handleUploadFile}
                isGenerating={isGenerating}
                streamingStalled={streamingStalled}
                activityItems={activityItems}
                agentTimelineItems={agentTimelineItems}
                turns={turns}
                activityError={activityError}
                deckId={activeDeck?.id}
                collapsed={true}
                onToggleCollapse={() => setChatCollapsed(false)}
                agentOnline={agentOnline}
                agentBooting={agentBooting}
                profiles={profiles}
                confluenceOpen={confluenceOpen}
                onToggleConfluence={() => setConfluenceOpen((v) => !v)}
                droploftOpen={droploftOpen}
                onToggleDroploft={() => setDroploftOpen((v) => !v)}
                referencedPages={referencedPages}
                onRemovePageReference={handleRemoveReference}
                currentTulpa={currentTulpa}
                onLoadMore={loadMoreMessages}
                hasMoreMessages={hasMoreMessages}
                loadingMoreMessages={loadingMoreMessages}
                onArtifactCardClick={setArtifactView}
              />
            ) : (
              <div style={{ width: panelOpen ? chatWidth : '100%', flexShrink: 0, display: 'flex', flexDirection: 'column', transition: isDragging ? 'none' : 'width 0.4s ease' }}>
                <ChatPanel
                  messages={messages}
                  onSend={sendMessage}
                  onStop={stopGeneration}
                  onClearMessages={clearMessages}
                  onUploadFile={handleUploadFile}
                  isGenerating={isGenerating}
                  activityItems={activityItems}
                  agentTimelineItems={agentTimelineItems}
                  turns={turns}
                  activityError={activityError}
                  deckId={activeDeck?.id}
                  collapsed={false}
                  onToggleCollapse={() => setChatCollapsed(true)}
                  agentOnline={agentOnline}
                  agentBooting={agentBooting}
                  profiles={profiles}
                  confluenceOpen={confluenceOpen}
                  onToggleConfluence={() => setConfluenceOpen((v) => !v)}
                  droploftOpen={droploftOpen}
                  onToggleDroploft={() => setDroploftOpen((v) => !v)}
                  referencedPages={referencedPages}
                  onRemovePageReference={handleRemoveReference}
                  currentTulpa={currentTulpa}
                  onLoadMore={loadMoreMessages}
                  hasMoreMessages={hasMoreMessages}
                  loadingMoreMessages={loadingMoreMessages}
                  onArtifactCardClick={setArtifactView}
                  />
              </div>
            )}

            {/* Resizable divider — subtle line, wider hit area, handle on hover */}
            {!chatCollapsed && panelOpen && (
              <div
                ref={dividerRef}
                onMouseDown={handleDividerMouseDown}
                className="pp-divider"
                style={{
                  width: 12, flexShrink: 0, cursor: 'col-resize',
                  background: 'transparent', position: 'relative',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                }}
              >
                {/* Thin line — hidden by default, visible on hover */}
                <div style={{
                  width: 1, height: '100%',
                  background: 'transparent',
                  transition: 'background 0.2s',
                }} className="pp-divider-line" />
                {/* Drag handle — visible on hover */}
                <div className="pp-divider-handle" style={{
                  position: 'absolute',
                  width: 4, height: 32, borderRadius: 2,
                  background: 'var(--text-muted)',
                  opacity: 0, transition: 'opacity 0.2s',
                }} />
              </div>
            )}
            <style>{`
              .pp-divider:hover .pp-divider-handle { opacity: 0.5 !important; }
              .pp-divider:hover .pp-divider-line { background: var(--border) !important; }
              .pp-divider:active .pp-divider-handle { opacity: 0.8 !important; }
              @media (max-width: 700px) {
                .pp-workspace-layout {
                  flex-direction: column-reverse !important;
                }
                .pp-workspace-layout > div:first-child {
                  width: 100% !important;
                  flex: 1 !important;
                  min-height: 0;
                }
                .pp-divider { display: none !important; }
                .pp-preview-panel {
                  width: 100% !important;
                  flex: none !important;
                  aspect-ratio: 4/3;
                  max-height: 61.8vh;
                }
              }
            `}</style>


            {/* Right: Preview / Storyboard — hidden until content generates */}
            <div className="pp-preview-panel" style={{
              flex: panelOpen ? 1 : 0,
              minWidth: 0,
              width: panelOpen ? undefined : 0,
              overflow: panelOpen ? undefined : 'hidden',
              visibility: panelOpen ? 'visible' : 'hidden',
              display: 'flex', flexDirection: 'column', position: 'relative',
              transition: isDragging ? 'none' : 'flex 0.4s ease, width 0.4s ease',
            }}>
              {isDragging && <div style={{ position: 'absolute', inset: 0, zIndex: 50 }} />}
              <PreviewPanel
                phase={computePanelPhase(activeDeck, contextItems, artifacts)}
                deckId={activeDeck.id}
                workspaceId={activeWorkspaceId}
                deckTitle={activeDeck.title}
                deckMode={activeDeck.mode ?? null}
                deckTheme={activeDeck.theme ?? null}
                deckCurrentVersion={activeDeck.current_version ?? null}
                onDeckChanged={() => {
                  // Refetch the deck so deckMode/deckTheme update after
                  // BuildModePicker submits.
                  api.getDeck(activeDeck.id).then(setActiveDeck).catch(() => {});
                }}
                isGenerating={isGenerating}
                currentActivity={currentActivity}
                onAdvancePhase={advancePhase}
                planMarkdown={planMarkdown}
                onPlanMarkdownChange={handlePlanMarkdownChange}
                onPlanSave={handlePlanSave}
                audienceInfo={(() => { try { const c = contextItems.find(c => c.key === 'memory/audience.json'); return c?.content ? JSON.parse(c.content) : null; } catch { return null; } })()}
                goalInfo={(() => { try { const c = contextItems.find(c => c.key === 'memory/goal.json'); const g = c?.content ? JSON.parse(c.content) : null; return g?.outcome ? { outcome: g.outcome, duration: g.duration } : null; } catch { return null; } })()}
                keyMessages={(() => { try { const c = contextItems.find(c => c.key === 'memory/goal.json'); const g = c?.content ? JSON.parse(c.content) : null; return g?.key_messages?.length > 0 ? g.key_messages : null; } catch { return null; } })()}
                maxPhaseReached={maxPhaseReached}
                previewRefreshKey={previewRefreshKey}
                contextVersion={conceptCloudVersion}
                contextItems={contextItems}
                contextHydratedDeckId={contextHydratedDeckId}
                planTask={planTask}
                previewTask={previewTask}
                notesTask={notesTask}
                planSaveStatus={planSaveStatus}
                planUpdatedAt={contextItems.find(c => c.key === activePlanKey)?.updated_at}
                planVersions={planVersions}
                activePlanKey={activePlanKey}
                onSelectPlanVersion={(key: string) => {
                  setActivePlanKey(key);
                  const item = contextItems.find(c => c.key === key);
                  if (item?.content) setPlanMarkdown(item.content);
                }}
                notesMarkdown={notesMarkdown}
                onNotesChange={handleNotesChange}
                onNotesSave={handleNotesSave}
                notesSaveStatus={notesSaveStatus}
                notesUpdatedAt={contextItems.find(c => c.key === activeNotesKey)?.updated_at}
                notesVersions={notesVersions}
                activeNotesKey={activeNotesKey}
                onSelectNotesVersion={(key: string) => {
                  setActiveNotesKey(key);
                  const item = contextItems.find(c => c.key === key);
                  if (item?.content) setNotesMarkdown(item.content);
                }}
                previewGeneratingNoHTML={previewGeneratingNoHTML}
                artifacts={artifacts}
                artifactSignals={activeDeck?.signals?.artifacts}
                activeArtifactSignalId={activeDeck?.signals?.active_artifact_id ?? null}
                activeArtifactKey={activeArtifactKey}
                onSelectArtifact={(key) => {
                  setActiveArtifactKey(key);
                  setPreviewRefreshKey(prev => prev + 1);
                }}
                activeSlideHash={activeSlideHash}
                onActiveTabChange={() => {
                  // Panel always takes major ratio — no tab-based ratio switching
                }}
                artifactView={artifactView}
                onCloseArtifactView={() => setArtifactView(null)}
                onPublishToDroploft={
                  droploftConnections.length > 0
                    ? async () => {
                        if (!activeDeck) return;
                        setDroploftPublishing(true);
                        try {
                          const r = await api.publishDroploftDeck(activeDeck.id, {
                            connectionId: droploftConnections[0]!.id,
                          });
                          if (r.url) window.open(r.url, '_blank');
                        } catch (err) {
                          // Surface to chat as a system note so the user
                          // sees what went wrong (esp. the 1 MB cap).
                          const msg = err instanceof Error ? err.message : String(err);
                          setMessages((prev) => [
                            ...prev,
                            {
                              id: `sys-${Date.now()}`,
                              role: 'system' as const,
                              content: `Droploft publish failed: ${msg}`,
                              timestamp: Date.now(),
                            },
                          ]);
                        } finally {
                          setDroploftPublishing(false);
                        }
                      }
                    : undefined
                }
                droploftPublishing={droploftPublishing}
              />
              {viewerFile && (() => {
                // Find the full key from contextItems to build download URL
                const ctxItem = contextItems.find(c => c.key.endsWith(viewerFile.name));
                const dlUrl = activeDeck && ctxItem
                  ? api.contextDownloadUrl(activeDeck.id, ctxItem.key)
                  : undefined;
                return (
                  <FileViewerDrawer
                    key={viewerFile.name}
                    name={viewerFile.name}
                    content={viewerFile.content}
                    onClose={() => setViewerFile(null)}
                    downloadUrl={dlUrl}
                  />
                );
              })()}
            </div>
          </>
        )}
      </div>

      {/* Floating panels */}
      <DebugPanel
        open={debugOpen}
        onClose={() => setDebugOpen(false)}
        contextItems={effectiveContextItems}
        mode={editMode ? 'manage' : 'view'}
        activeFileName={editMode?.activeFile}
        disabledKeys={disabledKeys}
        pendingToggleKeys={pendingToggleKeys}
        onToggleFile={handleToggleFile}
        onViewFile={(name, content) => {
          if (editMode) {
            setEditMode(prev => prev ? { ...prev, activeFile: name } : prev);
          } else {
            setViewerFile({ name, content });
          }
        }}
        onDeleteFile={(name) => {
          if (!editMode) return;
          setEditMode(prev => {
            if (!prev) return prev;
            const newFiles = prev.files.filter(f => f.name !== name);
            return { files: newFiles, activeFile: prev.activeFile === name ? (newFiles[0]?.name || null) : prev.activeFile };
          });
        }}
        onAddFile={(section, skillGroup) => {
          if (!editMode) return;
          const newName = `new-file-${Date.now()}.md`;
          const newFile = { id: `file-${Date.now()}`, name: newName, content: '', section, skillGroup };
          setEditMode(prev => prev ? { files: [...prev.files, newFile], activeFile: newName } : prev);
        }}
        onManage={handleManage}
        onEditSave={handleEditSave}
        onEditDiscard={() => setEditMode(null)}
        artifacts={artifacts}
        onViewArtifact={() => {
          if (!activeDeck) return;
          window.open(`/api/decks/${activeDeck.id}/preview`, '_blank');
        }}
      />
      <ConfluencePanel
        open={confluenceOpen}
        onClose={() => setConfluenceOpen(false)}
        confluenceConnections={confluenceConnections}
        confluenceSpaces={confluenceSpaces}
        confluencePages={confluencePages}
        confluenceLoading={confluenceLoading}
        selectedSpaceId={selectedSpaceId}
        onConnectConfluence={handleConnectConfluence}
        onDisconnectConfluence={async (connId) => {
          try {
            await api.deleteConfluenceConnection(connId);
            setConfluenceConnections([]);
            setConfluenceSpaces([]);
            setConfluencePages([]);
          } catch {}
        }}
        onSelectSpace={handleSelectSpace}
        onReferencePage={handleReferencePage}
        onRemoveReference={handleRemoveReference}
        referencingPageIds={referencingPageIds}
        confluenceExpandedPages={confluenceExpandedPages}
        confluenceChildPages={confluenceChildPages}
        confluenceLoadingChildren={confluenceLoadingChildren}
        onToggleExpandPage={handleToggleExpandPage}
        confluenceSpacesError={confluenceSpacesError}
        onRetrySpaces={() => {
          if (activeDeck) {
            setConfluenceSpacesError(null);
            fetchConfluenceSpaces(activeDeck.id);
          }
        }}
        onConfirmDone={(titles) => {
          if (titles.length > 0) {
            const fileList = titles.join(', ');
            const agentMsg = `I added ${titles.length} Confluence page(s): ${fileList}. You can read them from /home/user/context/confluence/. Acknowledge briefly.`;
            sendMessageRef.current?.(agentMsg, {
              displayContent: titles.join('\n'),
              messageType: 'file',
            });
          }
        }}
        chatWidth={chatWidth}
      />

      <DroploftPanel
        open={droploftOpen}
        onClose={() => setDroploftOpen(false)}
        deckId={activeDeck?.id}
        onImported={(titles) => {
          if (titles.length === 0) return;
          const fileList = titles.join(', ');
          const agentMsg = `I added ${titles.length} Droploft doc(s): ${fileList}. They live under /work/deck/context/droploft/. Acknowledge briefly.`;
          sendMessageRef.current?.(agentMsg, {
            displayContent: titles.join('\n'),
            messageType: 'file',
          });
        }}
      />
      {/* Confirm dialog: agent updated plan while user has unsaved edits */}
      {pendingAgentPlan !== null && (
        <PlanConflictDialog
          mine={planMarkdown}
          theirs={pendingAgentPlan}
          onKeepMine={() => {
            // Save user's version to Arx (overwrite agent's) + inject into agent container
            if (activeDeck) {
              api.saveContext(activeDeck.id, 'memory/storyboard.md', planMarkdown).catch(() => {});
              planLastSavedRef.current = planMarkdown;
            }
            planDirtyRef.current = false;
            setPendingAgentPlan(null);
          }}
          onUseTheirs={() => {
            setPlanMarkdown(pendingAgentPlan!);
            planLastSavedRef.current = pendingAgentPlan!;
            planDirtyRef.current = false;
            setPendingAgentPlan(null);
          }}
        />
      )}

      {/* First-login product tour — gates on a localStorage flag so
          returning users aren't bothered. Renders nothing for repeat
          visits. */}
      <OnboardingTour enabled={!!user} />
    </div>
  );
}

export default function AppRoot() {
  return (
    <IconContext.Provider value={ICON_DEFAULTS}>
      <AppInner />
      <NotificationBanner />
    </IconContext.Provider>
  );
}
